import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { MONTAGE_LABELS, getObturateurAttenuationLabel, getObturateurIllustration, getEmboutFormeImage, getObturateurFormeLabel, getEmboutFormeLabel, getPuissanceDisplay } from './orderFormConstants';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ear, SignOut, MagnifyingGlass, CheckCircle, Download, Clock, Package, Users, Printer, Plus, Eye, ClockCounterClockwise, File, MapPin, ChartBar, CaretDown, CaretUp, PencilSimple, Hammer, PaperPlaneTilt, XCircle, Trash, Cube, Timer, HourglassHigh, UploadSimple, Warning, Wrench, Pause, Barcode, Flask } from '@phosphor-icons/react';
import ManualOrderDialog from './ManualOrderDialog';
import Validation3DSection from './Validation3DSection';
import BatchUpload3DDialog from './BatchUpload3DDialog';
import { TechStats } from './StatsCharts';
import { ColorSwatch } from './OrderFormHelpers';
import { getOrderBusinessHours, formatBusinessHours, getUrgencyLevel } from '@/utils/businessHours';
import AuthImage from '@/components/AuthImage';
import StartProductionDialog from './StartProductionDialog';
import Cq01Dialog from './Cq01Dialog';

// Helper : libellé humain d'une couleur pour l'affichage dans le compte-rendu tech.
const COULEUR_LABELS = {
  transparent: 'Transparent',
  rose: 'Rosé',
  bleu_rouge: 'Bleu/Rouge',
  rouge_bleu: 'Rouge (D) / Bleu (G)',
  rouge: 'Rouge',
  bleu: 'Bleu',
  orange: 'Orange',
  noir: 'Noir',
  fuchsia: 'Fuchsia',
};
const couleurLabel = (c) => COULEUR_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : '—');

function CounterBadge({ hours, level, hasStl, threshold = 72, isPaused = false }) {
  if (level === 'none') {
    return <span className="text-xs text-zinc-400" data-testid="counter-badge">—</span>;
  }
  const display = formatBusinessHours(hours);
  const pct = Math.min(100, (hours / threshold) * 100);
  const dayLabel = threshold === 120 ? '5j' : '3j';
  // When paused: visually freeze the badge with a slate/blue tint and pause icon
  if (isPaused && level !== 'finished') {
    return (
      <div className="inline-flex flex-col gap-0.5 bg-blue-100 rounded px-2 py-1 min-w-[60px]" data-testid="counter-badge" title="Chronomètre en pause — validation 3D en attente">
        <div className="flex items-center gap-1 text-blue-800 text-xs font-bold">
          <Pause size={13} weight="fill" />
          <span>{display}</span>
          <span className="text-[9px] font-semibold opacity-70 ml-0.5" data-testid="counter-day-label">{dayLabel}</span>
        </div>
        <div className="w-full h-1 bg-white/60 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[9px] font-semibold text-blue-700 leading-tight" data-testid="counter-paused-label">Val. 3D ⏸</span>
      </div>
    );
  }
  const config = {
    ok: { bg: 'bg-emerald-100', text: 'text-emerald-800', bar: 'bg-emerald-500', icon: <Timer size={13} weight="bold" /> },
    warning: { bg: 'bg-yellow-100', text: 'text-yellow-800', bar: 'bg-yellow-500', icon: <HourglassHigh size={13} weight="bold" /> },
    critical: { bg: 'bg-orange-100', text: 'text-orange-800', bar: 'bg-orange-500', icon: <HourglassHigh size={13} weight="bold" /> },
    overdue: { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-600', icon: <HourglassHigh size={13} weight="bold" /> },
    finished: { bg: 'bg-zinc-100', text: 'text-zinc-600', bar: 'bg-zinc-400', icon: <CheckCircle size={13} weight="bold" /> },
  };
  const c = config[level] || config.ok;
  return (
    <div className={`inline-flex flex-col gap-0.5 ${c.bg} rounded px-2 py-1 min-w-[60px]`} data-testid="counter-badge">
      <div className={`flex items-center gap-1 ${c.text} text-xs font-bold`}>
        {c.icon}
        <span>{display}</span>
        <span className={`text-[9px] font-semibold opacity-70 ml-0.5 ${threshold === 120 ? 'text-blue-700' : ''}`} data-testid="counter-day-label">{dayLabel}</span>
        {level === 'finished' && <CheckCircle size={11} className="text-zinc-500" />}
        {!hasStl && level !== 'finished' && <span className="text-[9px] opacity-60 ml-0.5">STL?</span>}
      </div>
      <div className="w-full h-1 bg-white/60 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function TechDashboard() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandGroupFilter, setBrandGroupFilter] = useState('all');
  const [establishmentFilter, setEstablishmentFilter] = useState('all');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [startProdOrderId, setStartProdOrderId] = useState(null);
  const [cq01OrderId, setCq01OrderId] = useState(null);
  const [establishmentOptions, setEstablishmentOptions] = useState([]);
  const [stats, setStats] = useState({ pending: 0, design: 0, in_progress: 0, completed: 0, cancelled: 0, refabrications: 0 });
  const [detailedStats, setDetailedStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [readyDialogOpen, setReadyDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [readyResult, setReadyResult] = useState(null);

  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [val3dDialogOrder, setVal3dDialogOrder] = useState(null);
  const [, setTick] = useState(0);
  const stlInputRef = useRef(null);
  const [stlUploadOrderId, setStlUploadOrderId] = useState(null);
  const [editSerialLeft, setEditSerialLeft] = useState('');
  const [editSerialRight, setEditSerialRight] = useState('');
  const [editingSerial, setEditingSerial] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [emboutStats, setEmboutStats] = useState(null);
  const [emboutMonth, setEmboutMonth] = useState('');
  const [savBlock, setSavBlock] = useState({ blocked: false, urgent_sav_count: 0, urgent_savs: [], message: '' });
  const [savDialogOpen, setSavDialogOpen] = useState(false);
  const [savList, setSavList] = useState([]);
  const [savLoading, setSavLoading] = useState(false);
  const [savOpenCount, setSavOpenCount] = useState(0);
  const [savDetailItem, setSavDetailItem] = useState(null);
  const [savStlOrderId, setSavStlOrderId] = useState(null);
  const [savStlDialogOpen, setSavStlDialogOpen] = useState(false);
  const [savStlDialogOrder, setSavStlDialogOrder] = useState(null);
  const [savStlLeft, setSavStlLeft] = useState(null);
  const [savStlRight, setSavStlRight] = useState(null);
  const [savStlComment, setSavStlComment] = useState('');
  const [savStlUploading, setSavStlUploading] = useState(false);
  const [savStlSending, setSavStlSending] = useState(false);
  const [savStlUploaded, setSavStlUploaded] = useState({ left: false, right: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1 });

  useEffect(() => { fetchOrders(); fetchStats(); fetchEmboutStats(); fetchSavBlock(); fetchSavList(); }, [statusFilter, searchQuery, brandGroupFilter, establishmentFilter, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const oid = new URLSearchParams(window.location.search).get('order');
    if (!oid) return;
    axios.get(`${API}/orders/${oid}`).then(r => openDetailsDialog(r.data)).catch(() => toast.error('Commande introuvable'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadEstablishments = async () => {
      try {
        const [estRes, compRes] = await Promise.all([axios.get(`${API}/establishments`), axios.get(`${API}/companies`)]);
        const companies = Object.fromEntries((compRes.data || []).map(c => [c.id, c.name]));
        setEstablishmentOptions((estRes.data || []).map(e => ({ id: e.id, name: e.name, company: companies[e.company_id] || '', ville: e.ville || '' })));
      } catch (e) { console.error('Error loading establishments', e); }
    };
    loadEstablishments();
  }, []);

  // Update counters every 60s for active orders
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      let url = `${API}/orders`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery);
      if (brandGroupFilter !== 'all') params.append('brand_group', brandGroupFilter);
      if (establishmentFilter !== 'all') params.append('establishment_id', establishmentFilter);
      params.append('page', currentPage);
      params.append('per_page', 50);
      url += `?${params.toString()}`;
      const response = await axios.get(url);
      const data = response.data;
      const ordersList = data.orders || data;
      // An order is considered "urgent" only while it's still being worked on.
      // Once completed, cancelled or shipped, it must NOT bubble to the top
      // and the red URGENT badge must disappear.
      // Generic SAV refab orders are sorted like any other order (no special head-of-list privilege).
      const isUrgentActive = (o) => o.is_sav_urgent && !['completed', 'cancelled', 'shipped'].includes(o.status);
      const sorted = ordersList.sort((a, b) => {
        const au = isUrgentActive(a), bu = isUrgentActive(b);
        if (au && !bu) return -1;
        if (!au && bu) return 1;
        return 0;
      });
      setOrders(sorted);
      if (data.pagination) setPagination(data.pagination);
    } catch (error) { toast.error('Erreur lors du chargement'); } finally { setLoading(false); }
  };

  const fetchStats = async () => { try { const r = await axios.get(`${API}/dashboard/stats`); setStats(r.data); } catch (e) { console.error('Stats fetch error:', e.message); } };
  const fetchDetailedStats = async () => { try { const r = await axios.get(`${API}/dashboard/stats-detailed`); setDetailedStats(r.data); } catch (e) { console.error('Detailed stats fetch error:', e.message); } };
  const fetchEmboutStats = async (month) => {
    try {
      const url = month ? `${API}/dashboard/embout-stats?month=${month}` : `${API}/dashboard/embout-stats`;
      const r = await axios.get(url);
      setEmboutStats(r.data);
      if (!emboutMonth) setEmboutMonth(r.data.month);
    } catch (e) { console.error('Embout stats fetch error:', e.message); }
  };
  const fetchSavBlock = async () => {
    try {
      const r = await axios.get(`${API}/dashboard/sav-block-status`);
      setSavBlock(r.data);
    } catch (e) { console.error('SAV block fetch error:', e.message); }
  };
  const fetchSavList = async () => {
    setSavLoading(true);
    try {
      const r = await axios.get(`${API}/sav`);
      setSavList(r.data);
      setSavOpenCount(r.data.filter(s => s.status !== 'cloturee').length);
    } catch (e) { console.error('SAV list fetch error:', e.message); }
    finally { setSavLoading(false); }
  };
  const openSavDialog = () => {
    fetchSavList();
    setSavDialogOpen(true);
  };
  const openSavStlDialog = (order) => {
    setSavStlDialogOrder(order);
    setSavStlLeft(null);
    setSavStlRight(null);
    setSavStlComment('');
    setSavStlUploaded({ left: false, right: false });
    setSavStlDialogOpen(true);
  };
  const handleDownloadSingleSTL = async (stlFile) => {
    try {
      const res = await axios.get(`${API}/files/${stlFile.file_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = res.headers['x-filename'] || stlFile.filename || 'fichier.stl';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Erreur telechargement'); }
  };
  const handleSavStlUploadSide = async (file, side) => {
    if (!file || !savStlDialogOrder) return;
    setSavStlUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/orders/${savStlDialogOrder.id}/upload-stl?side=${side}`, fd);
      const version = res.data?.version || 1;
      setSavStlUploaded(prev => ({ ...prev, [side]: version }));
      toast.success(`STL ${side === 'left' ? 'gauche' : 'droit'} V${version} uploade`);
      // Refresh order data
      fetchOrders();
    } catch (err) { toast.error('Erreur upload STL'); }
    finally { setSavStlUploading(false); }
  };
  const handleSavSendToClient = async () => {
    if (!savStlDialogOrder) return;
    setSavStlSending(true);
    try {
      // Find the SAV request linked to this order
      const savReq = savList.find(s => s.refabrication_order_id === savStlDialogOrder.id || s.order_id === savStlDialogOrder.sav_id);
      const savId = savStlDialogOrder.sav_id || savReq?.id;
      if (savId) {
        await axios.post(`${API}/sav/${savId}/send-to-client`, { admin_note: savStlComment });
        toast.success('Visuel envoye au client');
      } else {
        // Fallback: upload as validation3d
        const sides = [];
        if (savStlUploaded.left) sides.push('left');
        if (savStlUploaded.right) sides.push('right');
        toast.success('STL uploade(s) avec succes');
      }
      setSavStlDialogOpen(false);
      fetchOrders();
      fetchSavBlock();
      fetchSavList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur envoi'); }
    finally { setSavStlSending(false); }
  };
  const openSavDetail = async (savId) => {
    try {
      const r = await axios.get(`${API}/sav/${savId}`);
      setSavDetailItem(r.data);
    } catch { toast.error('Erreur chargement SAV'); }
  };
  const handleLogout = () => { logout(); navigate('/login'); };

  const triggerStlUpload = (orderId) => {
    setStlUploadOrderId(orderId);
    stlInputRef.current?.click();
  };
  const handleStlFileSelected = async (e) => {
    const files = e.target.files;
    if (!files?.length || !stlUploadOrderId) return;
    try {
      for (const file of files) {
        const side = file.name.toLowerCase().includes('gauche') || file.name.toLowerCase().includes('left') || file.name.toLowerCase().includes('_og') || file.name.toLowerCase().includes('_l.') ? 'left' : 'right';
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/orders/${stlUploadOrderId}/upload-stl?side=${side}`, formData);
      }
      toast.success(`${files.length} fichier(s) STL uploadé(s)`);
      fetchOrders();
    } catch (err) {
      toast.error('Erreur lors de l\'upload STL');
    } finally {
      setStlUploadOrderId(null);
      e.target.value = '';
    }
  };

  const startEditSerial = (order) => {
    setEditSerialLeft(order.serial_number_left || '');
    setEditSerialRight(order.serial_number_right || '');
    setEditingSerial(true);
  };

  const saveSerial = async () => {
    if (!detailsOrder) return;
    try {
      const body = {};
      if (editSerialLeft) body.serial_number_left = editSerialLeft;
      if (editSerialRight) body.serial_number_right = editSerialRight;
      await axios.put(`${API}/orders/${detailsOrder.id}/serial-number`, body);
      toast.success('Numéro de série modifié');
      setEditingSerial(false);
      setDetailsOrder({ ...detailsOrder, serial_number_left: editSerialLeft || detailsOrder.serial_number_left, serial_number_right: editSerialRight || detailsOrder.serial_number_right });
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleDownloadSerialPng = async (order, side) => {
    if (!order?.id) return;
    try {
      const res = await axios.get(`${API}/orders/${order.id}/serial-label-png?side=${side}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const serial = side === 'left' ? order.serial_number_left : order.serial_number_right;
      const sideCode = side === 'left' ? 'OG' : 'OD';
      const safeRef = (order.patient_reference || 'ORDER').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 30);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hamac_${safeRef}_${sideCode}_${serial}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur téléchargement étiquette PNG');
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  // Impression du bon de commande : clone du contenu du dialogue « Détails » dans une
  // fenêtre dédiée, avec les styles de l'app, sans boutons/inputs ni sections techniques.
  const handlePrintOrderSheet = () => {
    const root = document.getElementById('order-sheet-print-root');
    if (!root || !detailsOrder) { toast.error('Impossible de préparer l\'impression'); return; }
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) { toast.error('Fenêtre bloquée : autorisez les pop-ups pour imprimer'); return; }
    const headStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map(n => n.outerHTML).join('\n');
    const serials = [detailsOrder.serial_number_right && `OD ${detailsOrder.serial_number_right}`, detailsOrder.serial_number_left && `OG ${detailsOrder.serial_number_left}`].filter(Boolean).join(' · ');
    const esc = (v) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    win.document.open();
    win.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Bon de commande ${esc(detailsOrder.patient_reference)}</title>
${headStyles}
<style>
  @page { size: A4; margin: 12mm; }
  html, body { background: #fff !important; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #18181b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .print-header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #18181b; padding-bottom: 8px; margin-bottom: 14px; }
  .print-header h1 { font-size: 20px; margin: 0; letter-spacing: .02em; }
  .print-header .meta { text-align: right; font-size: 12px; color: #52525b; }
  .print-header .meta strong { color: #18181b; font-size: 14px; }
  #order-sheet-print-root button, #order-sheet-print-root input, #order-sheet-print-root textarea, #order-sheet-print-root select, [data-no-print] { display: none !important; }
  #order-sheet-print-root { max-height: none !important; overflow: visible !important; }
  #order-sheet-print-root > * { break-inside: avoid; }
  .print-footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #d4d4d8; font-size: 10px; color: #71717a; display: flex; justify-content: space-between; }
</style></head><body>
<div class="print-header">
  <div><h1>HAMAC SOLUTIONS — Bon de commande</h1><div style="font-size:12px;color:#52525b;margin-top:2px;">${esc(detailsOrder.client_company || detailsOrder.client_name || '')}${detailsOrder.establishment_name && detailsOrder.establishment_name !== detailsOrder.client_company ? ' · ' + esc(detailsOrder.establishment_name) : ''}</div></div>
  <div class="meta"><div>Réf. patient <strong>${esc(detailsOrder.patient_reference)}</strong></div>${serials ? `<div>N° série <strong>${esc(serials)}</strong></div>` : ''}<div>Commande du ${esc(new Date(detailsOrder.created_at).toLocaleDateString('fr-FR'))}</div></div>
</div>
${root.outerHTML}
<div class="print-footer"><span>Imprimé le ${esc(new Date().toLocaleString('fr-FR'))} par ${esc(user?.name || '')}</span><span>Commande ${esc(detailsOrder.id)}</span></div>
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.focus(); window.print(); }, 350); });</script>
</body></html>`);
    win.document.close();
  };

  const handleGroupedShippingLabels = async () => {
    if (selectedOrders.size < 1) { toast.error('Sélectionnez au moins une commande'); return; }
    try {
      const res = await axios.post(`${API}/orders/grouped-shipping-labels`, { order_ids: Array.from(selectedOrders) }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      const n = res.headers['x-parcel-count'];
      if (n) toast.success(`${n} étiquette(s) adresse générée(s)`);
    } catch (err) {
      let detail = 'Erreur lors de la génération des étiquettes';
      try { detail = JSON.parse(await err.response?.data?.text())?.detail || detail; } catch { /* blob non JSON */ }
      toast.error(detail);
    }
  };

  const handleGroupedSlip = async () => {
    if (selectedOrders.size < 2) { toast.error('Sélectionnez au moins 2 commandes'); return; }
    try {
      const res = await axios.post(`${API}/orders/grouped-delivery-slip`, { order_ids: Array.from(selectedOrders) }, { responseType: 'text' });
      const w = window.open('', '_blank');
      if (!w) { toast.error('Popup bloqué. Autorisez les popups.'); return; }
      const doc = w.document;
      doc.open();
      doc.write(DOMPurify.sanitize(res.data, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style', 'link'] }));
      doc.close();
      setSelectedOrders(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération du bordereau groupé');
    }
  };

  const openDetailsDialog = async (order) => {
    // Block if SAV blocked and not a SAV refab order
    if (savBlock.blocked && !order.is_sav_refabrication && user?.role === 'technician') {
      toast.error('Acces bloque — Finalisez les SAV urgents avant de consulter les commandes.');
      return;
    }
    try {
      const response = await axios.get(`${API}/orders/${order.id}`);
      setDetailsOrder(response.data);
      setDetailsDialogOpen(true);
    } catch (e) {
      setDetailsOrder(order);
      setDetailsDialogOpen(true);
    }
  };

  const handleEditOrder = async (order) => {
    try {
      const response = await axios.get(`${API}/orders/${order.id}`);
      setEditingOrder(response.data);
    } catch (e) {
      setEditingOrder(order);
    }
  };

  const askConfirm = (type, orderId) => {
    if (type === 'start-fabrication') { setStartProdOrderId(orderId); return; }
    if (type === 'ready') { setCq01OrderId(orderId); return; }
    setConfirmAction({ type, orderId });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, orderId } = confirmAction;

    // SAV block: prevent starting new work (technician only - admin is never blocked)
    if (savBlock.blocked && !isAdmin && (type === 'start-design' || type === 'start-fabrication')) {
      const order = orders.find(o => o.id === orderId);
      // Allow continuing already-started orders (design -> fabrication is ok if already in design)
      if (type === 'start-design' && order?.status === 'pending') {
        toast.error('Des refabrications SAV prioritaires doivent etre finalisees avant de demarrer de nouvelles commandes.');
        setConfirmAction(null);
        return;
      }
    }

    setConfirmAction(null);
    try {
      if (type === 'start-design') {
        await axios.post(`${API}/orders/${orderId}/start-design`);
        toast.success('Design démarré');
      } else if (type === 'start-fabrication') {
        await axios.post(`${API}/orders/${orderId}/start-fabrication`);
        toast.success('Fabrication démarrée');
      } else if (type === 'ready') {
        const order = orders.find(o => o.id === orderId);
        const r = await axios.post(`${API}/orders/${orderId}/ready`);
        setSelectedOrder(order);
        setReadyResult(r.data);
        setReadyDialogOpen(true);
      } else if (type === 'cancel') {
        await axios.post(`${API}/orders/${orderId}/cancel`);
        toast.success('Commande annulée');
      } else if (type === 'delete') {
        await axios.delete(`${API}/orders/${orderId}`);
        toast.success('Commande supprimée');
      }
      fetchOrders();
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadSTL = async (order) => {
    for (const f of order.stl_files) {
      try {
        const r = await axios.get(`${API}/files/${f.file_id}/download`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = r.headers['x-filename'] || f.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) { toast.error('Erreur téléchargement'); }
    }
  };

  const printProductLabel = async (id) => {
    try {
      const res = await axios.get(`${API}/orders/${id}/label-product-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Erreur génération étiquette produit'); }
  };

  const printShippingLabel = async (id) => {
    try {
      const res = await axios.get(`${API}/orders/${id}/label-shipping-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Erreur génération étiquette adresse'); }
  };

  const openBonLivraison = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/bon-livraison`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Erreur bon de livraison'); }
  };

  const getStatusBadge = (s, t, o) => {
    // Only show URGENT/Refab SAV decorations while the order is still active
    const isActive = !['completed', 'cancelled', 'shipped'].includes(s);
    if (o?.is_sav_urgent && isActive) return <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-sm animate-pulse" data-testid="badge-sav-urgent">URGENT SAV</span>;
    if (o?.is_sav_refabrication && isActive) return <span className="px-2 py-1 bg-rose-500 text-white text-xs font-bold rounded-sm" data-testid="badge-sav-refab">Refab SAV</span>;
    if (t?.includes('refabrication')) return <span className="badge-refabrication">Refab</span>;
    if (s === 'pending') return <span className="badge-pending">En attente</span>;
    if (s === 'design') return <span className="badge-design">Design</span>;
    if (s === 'in_progress') return <span className="badge-in-progress">Fabrication</span>;
    if (s === 'completed') return <span className="badge-completed">Terminee</span>;
    if (s === 'cancelled') return <span className="badge-cancelled">Annulee</span>;
    return <span className="px-2 py-1 bg-zinc-200 text-xs rounded-sm">{s}</span>;
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatShortDate = (d) => { if (!d) return '—'; const date = new Date(d); return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR'); };

  // Urgence: dynamique (72h normal, 120h pour embout intégral)
  const getUrgencyData = (order) => {
    const result = getOrderBusinessHours(order);
    if (!result) return { style: {}, className: '', hours: 0, level: 'none', hasStl: false, threshold: 72, isPaused: false };
    const { hours, isFinished, hasStl, threshold, isPaused } = result;
    const level = getUrgencyLevel(hours, isFinished, threshold);
    const styleMap = {
      ok: { '--urgency-bg': '#dcfce7', '--urgency-color': '#14532d' },
      warning: { '--urgency-bg': '#fef9c3', '--urgency-color': '#713f12' },
      critical: { '--urgency-bg': '#ffedd5', '--urgency-color': '#7c2d12' },
      overdue: { '--urgency-bg': '#fee2e2', '--urgency-color': '#7f1d1d' },
      finished: {},
      none: {}
    };
    return {
      style: styleMap[level] || {},
      className: level !== 'finished' && level !== 'none' ? 'urgency-row' : '',
      hours,
      level,
      hasStl,
      threshold,
      isPaused: !!isPaused,
    };
  };

  const toggleStats = () => {
    if (!showStats && !detailedStats) fetchDetailedStats();
    setShowStats(!showStats);
  };

  const refreshAfterCreate = () => { fetchOrders(); fetchStats(); fetchEmboutStats(); fetchSavBlock(); if (showStats) fetchDetailedStats(); };

  const handleDialogClose = (open) => {
    if (!open) {
      setOrderDialogOpen(false);
      setEditingOrder(null);
    }
  };

  const confirmDialogLabels = {
    'start-design': { title: 'Démarrer le design', desc: 'Confirmer la prise en charge de cette commande pour le design ?', color: 'bg-blue-600 hover:bg-blue-700' },
    'start-fabrication': { title: 'Démarrer la fabrication', desc: 'Confirmer le lancement de la fabrication ?', color: 'bg-amber-500 hover:bg-amber-600' },
    'ready': { title: 'Prêt à envoyer', desc: 'Confirmer que la fabrication est terminée et prête à être envoyée ?', color: 'bg-emerald-600 hover:bg-emerald-700' },
    'cancel': { title: 'Annuler la commande', desc: 'Êtes-vous sûr de vouloir annuler cette commande ?', color: 'bg-rose-600 hover:bg-rose-700' },
    'delete': { title: 'Supprimer la commande', desc: 'Cette action est irréversible. Supprimer définitivement cette commande annulée ?', color: 'bg-red-700 hover:bg-red-800' },
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="tech-dashboard">
      <header className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Ear size={24} weight="duotone" className="sm:w-7 sm:h-7" />
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight">HAMAC</span>
            <span className="text-zinc-500 text-xs sm:text-sm ml-1 sm:ml-2 hidden sm:inline">Dashboard</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {isAdmin && <Link to="/admin"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white"><Users size={18} className="sm:mr-2" /><span className="hidden sm:inline">Admin</span></Button></Link>}
            <span className="text-xs sm:text-sm text-zinc-400 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="logout-btn"><SignOut size={18} /></Button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 flex-1">
              <div className="flex items-center gap-2 sm:gap-3"><Clock size={20} className="text-rose-600 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.pending}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Attente</p></div></div>
              <div className="flex items-center gap-2 sm:gap-3"><PencilSimple size={20} className="text-blue-600 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.design || 0}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Design</p></div></div>
              <div className="flex items-center gap-2 sm:gap-3"><Hammer size={20} className="text-amber-500 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.in_progress}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Fab.</p></div></div>
              <div className="items-center gap-2 sm:gap-3 hidden sm:flex"><CheckCircle size={24} className="text-emerald-600" /><div><p className="text-2xl font-bold">{stats.completed}</p><p className="text-xs text-zinc-500 uppercase tracking-wider">Terminées</p></div></div>
              <div className="items-center gap-2 sm:gap-3 hidden sm:flex"><Package size={24} className="text-violet-600" /><div><p className="text-2xl font-bold">{stats.refabrications}</p><p className="text-xs text-zinc-500 uppercase tracking-wider">Refab.</p></div></div>
            </div>
            <Button variant="ghost" size="sm" onClick={toggleStats} className="ml-2 sm:ml-4 text-zinc-500 hover:text-zinc-900" data-testid="toggle-stats-btn">
              <ChartBar size={18} className="mr-1" />{showStats ? <CaretUp size={14} /> : <CaretDown size={14} />}
            </Button>
          </div>
          {showStats && <TechStats stats={detailedStats} />}

          {/* Embout Stats - Monthly embout-based with value */}
          {emboutStats && (
            <div className="mt-3 p-4 bg-white rounded border border-zinc-200" data-testid="embout-stats-section">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Embouts — Mois</span>
                <Select value={emboutMonth} onValueChange={(v) => { setEmboutMonth(v); fetchEmboutStats(v); }}>
                  <SelectTrigger className="w-40 h-7 text-xs rounded-sm bg-white" data-testid="embout-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {emboutStats.available_months?.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                {/* Left: counts */}
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm text-center relative group cursor-help" data-testid="embouts-under-72h">
                  <p className="text-2xl font-bold text-emerald-700">{emboutStats.embouts_under_72h}</p>
                  <p className="text-[10px] text-emerald-600 uppercase tracking-wider">&lt; 3 jours</p>
                  {emboutStats.details_under_72h?.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-white border border-emerald-200 shadow-lg rounded-sm p-2 min-w-[220px] max-h-[200px] overflow-y-auto" data-testid="tooltip-under-72h">
                      {emboutStats.details_under_72h.map((d, i) => (
                        <div key={`${d.patient_reference}-${d.serial_left}-${d.serial_right}`} className="text-xs py-1 border-b border-zinc-100 last:border-0">
                          <span className="font-medium">{d.patient_reference}</span>
                          {d.serial_left && <span className="text-zinc-400 ml-1">L:{d.serial_left}</span>}
                          {d.serial_right && <span className="text-zinc-400 ml-1">R:{d.serial_right}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-red-50 border border-red-200 p-3 rounded-sm text-center relative group cursor-help" data-testid="embouts-over-72h">
                  <p className="text-2xl font-bold text-red-700">{emboutStats.embouts_over_72h}</p>
                  <p className="text-[10px] text-red-600 uppercase tracking-wider">&ge; 3 jours</p>
                  {emboutStats.details_over_72h?.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-white border border-red-200 shadow-lg rounded-sm p-2 min-w-[220px] max-h-[200px] overflow-y-auto" data-testid="tooltip-over-72h">
                      {emboutStats.details_over_72h.map((d, i) => (
                        <div key={`${d.patient_reference}-${d.serial_left}-${d.serial_right}`} className="text-xs py-1 border-b border-zinc-100 last:border-0">
                          <span className="font-medium">{d.patient_reference}</span>
                          {d.serial_left && <span className="text-zinc-400 ml-1">L:{d.serial_left}</span>}
                          {d.serial_right && <span className="text-zinc-400 ml-1">R:{d.serial_right}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-rose-50 border border-rose-300 p-3 rounded-sm text-center" data-testid="embouts-sav-error">
                  <p className="text-2xl font-bold text-rose-700">{emboutStats.embouts_sav_error}</p>
                  <p className="text-[10px] text-rose-600 uppercase tracking-wider">SAV erreur</p>
                </div>
                {/* Right: values */}
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm text-center" data-testid="value-gained">
                  <p className="text-2xl font-bold text-emerald-700">+{emboutStats.value_gained.toFixed(0)}€</p>
                  <p className="text-[10px] text-emerald-600 uppercase tracking-wider">Valeur gagnee</p>
                </div>
                <div className="bg-rose-50 border border-rose-300 p-3 rounded-sm text-center" data-testid="value-lost">
                  <p className="text-2xl font-bold text-rose-700">-{emboutStats.value_lost.toFixed(0)}€</p>
                  <p className="text-[10px] text-rose-600 uppercase tracking-wider">Valeur perdue</p>
                </div>
                <div className={`p-3 rounded-sm text-center border ${emboutStats.net_value >= 0 ? 'bg-zinc-900 border-zinc-900' : 'bg-rose-600 border-rose-600'}`} data-testid="value-net">
                  <p className="text-2xl font-bold text-white">{emboutStats.net_value >= 0 ? '+' : ''}{emboutStats.net_value.toFixed(0)}€</p>
                  <p className="text-[10px] text-white/80 uppercase tracking-wider">Valeur nette</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Admin Quick Nav */}
        {isAdmin && (
          <div className="flex flex-wrap gap-1 mb-4 bg-zinc-50 border border-zinc-200 rounded-sm p-1.5" data-testid="admin-quick-nav">
            {[
              { tab: 'dashboard', label: 'Vue d\'ensemble', icon: ChartBar },
              { tab: 'users', label: 'Utilisateurs', icon: Users },
              { tab: 'products', label: 'Produits', icon: Package },
              { tab: 'orders', label: 'Commandes', icon: Package },
              { tab: 'exports', label: 'Export Odoo', icon: File },
              { tab: 'shopify', label: 'Shopify', icon: Package },
              { tab: 'client-accounts', label: 'Comptes', icon: Users },
              { tab: 'sav', label: 'SAV', icon: Wrench },
              { tab: 'sav-stats', label: 'Stats SAV', icon: ChartBar },
              { tab: 'tech-value', label: 'Valeur', icon: ChartBar },
            ].map(({ tab, label, icon: Icon }) => (
              <Link key={tab} to={`/admin?tab=${tab}`}>
                <Button variant="ghost" size="sm" className="rounded-sm text-[10px] sm:text-xs h-7 px-2 text-zinc-600 hover:text-zinc-900 hover:bg-white"
                  data-testid={`admin-nav-${tab}`}>
                  <Icon size={12} className="mr-1" />{label}
                </Button>
              </Link>
            ))}
          </div>
        )}

        {/* SAV URGENT BLOCK BANNER - only show for technicians */}
        {savBlock.blocked && !isAdmin && (
          <div className="bg-red-600 text-white px-4 py-3 rounded-sm mb-4 flex items-center gap-3" data-testid="sav-block-banner">
            <Warning size={24} weight="fill" className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">SAV URGENT — Nouvelles commandes bloquees</p>
              <p className="text-xs text-red-100">{savBlock.message}</p>
              <p className="text-xs text-red-200 mt-1">{savBlock.urgent_sav_count} SAV urgent(s) en attente de finalisation</p>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-3 mb-4 sm:mb-6">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button onClick={() => setOrderDialogOpen(true)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-xs sm:text-sm" data-testid="new-order-btn">
              <Plus size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Saisir commande</span>
              <span className="sm:hidden">Commande</span>
            </Button>
            <Link to="/batches">
              <Button variant="outline" className="rounded-sm text-xs sm:text-sm" data-testid="batch-management-btn">
                <Cube size={16} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Lots de fabrication</span>
                <span className="sm:hidden">Lots</span>
              </Button>
            </Link>
            <Button variant="outline" onClick={() => setBatchUploadOpen(true)} className="rounded-sm text-xs sm:text-sm" data-testid="batch-upload-3d-btn">
              <Eye size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Import STL 3D par lot</span>
              <span className="sm:hidden">STL 3D</span>
            </Button>
            <Button variant={savOpenCount > 0 ? "default" : "outline"} onClick={openSavDialog}
              className={`rounded-sm text-xs sm:text-sm ${savOpenCount > 0 ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
              data-testid="sav-dashboard-btn">
              <Wrench size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">SAV</span>
              {savOpenCount > 0 && <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded text-[11px] font-bold" data-testid="sav-count-badge">({savOpenCount})</span>}
            </Button>
            <Button variant="outline" onClick={() => navigate('/resin-lots')} className="rounded-sm text-xs sm:text-sm" data-testid="resin-lots-btn" title="Lots de résine actifs par bac / machine">
              <Flask size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Lots résine</span>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 flex-1">
            <form className="relative w-44 sm:w-56" onSubmit={(e) => { e.preventDefault(); const v = deviceSearch.trim(); if (v) navigate(`/device/${encodeURIComponent(v)}`); }} data-testid="device-search-form">
              <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
              <Input placeholder="ID HAMAC / n° série" value={deviceSearch} onChange={(e) => setDeviceSearch(e.target.value)} className="pl-9 rounded-sm bg-white text-sm font-mono" data-testid="device-search-input" title="Ouvrir la fiche de fabrication d'un dispositif (HM-000123 ou n° de série)" />
            </form>
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 rounded-sm bg-white text-sm" data-testid="search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 sm:w-48 rounded-sm bg-white text-xs sm:text-sm" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="in_progress">Fabrication</SelectItem>
                <SelectItem value="completed">Terminées</SelectItem>
                <SelectItem value="cancelled">Annulées</SelectItem>
              </SelectContent>
            </Select>
            <Select value={brandGroupFilter} onValueChange={setBrandGroupFilter}>
              <SelectTrigger className="w-28 sm:w-40 rounded-sm bg-white text-xs sm:text-sm" data-testid="brand-group-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Toutes marques</SelectItem>
                <SelectItem value="phonak">Phonak (+ Unitron/Hansaton)</SelectItem>
                <SelectItem value="oticon">Oticon (+ Bernafon/Philips)</SelectItem>
                <SelectItem value="wsa">Signia/Rexton/Biotone/Widex</SelectItem>
                <SelectItem value="autres">Autres marques</SelectItem>
              </SelectContent>
            </Select>
            <Select value={establishmentFilter} onValueChange={(v) => { setEstablishmentFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-32 sm:w-48 rounded-sm bg-white text-xs sm:text-sm" data-testid="establishment-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">Tous les établissements</SelectItem>
                {establishmentOptions.map(e => (
                  <SelectItem key={e.id} value={e.id} data-testid={`establishment-filter-option-${e.id}`}>
                    {e.name}{e.ville ? ` — ${e.ville}` : ''}{e.company && e.company !== e.name ? ` (${e.company})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grouped action bar */}
        {selectedOrders.size >= 2 && (
          <div className="bg-zinc-900 text-white px-4 py-2 flex items-center gap-3 rounded-sm mb-2" data-testid="grouped-actions-bar">
            <span className="text-sm">{selectedOrders.size} commandes sélectionnées</span>
            <Button size="sm" variant="secondary" onClick={handleGroupedSlip} className="rounded-sm text-xs" data-testid="grouped-slip-btn">
              <Printer size={14} className="mr-1" />Bordereau groupé
            </Button>
            <Button size="sm" variant="secondary" onClick={handleGroupedShippingLabels} className="rounded-sm text-xs" data-testid="grouped-labels-btn">
              <MapPin size={14} className="mr-1" />Étiquettes multi-colis
              {(() => { const n = new Set(orders.filter(o => selectedOrders.has(o.id)).map(o => o.establishment_id || (o.establishment_address || o.client_address || 'na').trim().toLowerCase())).size; return n > 1 ? ` (${n} colis)` : ''; })()}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedOrders(new Set())} className="text-zinc-400 hover:text-white text-xs">Annuler</Button>
          </div>
        )}

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center"><div className="spinner mx-auto mb-4" />Chargement...</div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center text-zinc-500"><Package size={48} className="mx-auto mb-4 text-zinc-300" /><p>Aucune commande trouvée</p></div>
          ) : (
            <Table className="dense-table">
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-8 p-1"><input type="checkbox" onChange={(e) => { if (e.target.checked) { setSelectedOrders(new Set(orders.map(o => o.id))); } else { setSelectedOrders(new Set()); } }} className="h-3.5 w-3.5" /></TableHead>
                  <TableHead className="label-overline">Saisie / Livraison</TableHead>
                  <TableHead className="label-overline">72h</TableHead>
                  <TableHead className="label-overline">Client</TableHead>
                  <TableHead className="label-overline hidden xl:table-cell">N° Série</TableHead>
                  <TableHead className="label-overline">Statut</TableHead>
                  <TableHead className="label-overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => {
                  const urgency = getUrgencyData(o);
                  return (
                  <TableRow key={o.id} style={urgency.style} className={`${urgency.className} ${o.order_type?.includes('refabrication') ? 'order-row-refabrication' : ''} ${o.is_sav_urgent && !['completed', 'cancelled', 'shipped'].includes(o.status) ? 'bg-red-50 border-l-4 border-l-red-600' : ''} ${o.is_sav_refabrication && !o.is_sav_urgent && !['completed', 'cancelled', 'shipped'].includes(o.status) ? 'bg-rose-50/50' : ''} ${savBlock.blocked && !o.is_sav_refabrication && !isAdmin ? 'opacity-40' : ''} ${o.status === 'cancelled' ? 'opacity-60' : ''} table-row-hover`} data-testid={`order-row-${o.id}`}>
                    <TableCell className="p-1"><input type="checkbox" checked={selectedOrders.has(o.id)} onChange={() => toggleOrderSelection(o.id)} className="h-3.5 w-3.5" data-testid={`select-${o.id}`} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap leading-tight">
                      <div className="text-zinc-700">{formatShortDate(o.created_at)}</div>
                      {o.delivery_date ? (
                        <div className="font-bold text-red-600 text-[11px]" data-testid={`delivery-date-${o.id}`}>{formatShortDate(o.delivery_date)}</div>
                      ) : (
                        <div className="text-zinc-300 text-[11px]">—</div>
                      )}
                      {o.is_sav_refabrication && o.status !== 'completed' && (
                        <p className="text-[9px] text-violet-600 font-bold mt-0.5">Refab SAV</p>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap" data-testid={`counter-${o.id}`}>
                      <CounterBadge hours={urgency.hours} level={urgency.level} hasStl={urgency.hasStl} threshold={urgency.threshold} isPaused={urgency.isPaused} />
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-sm leading-tight">{o.client_name}</p>
                      <p className="text-[11px] text-zinc-500 hidden sm:block leading-tight">{o.client_company}</p>
                      {o.establishment_name && o.establishment_name !== o.client_company && (
                        <p className="text-[11px] text-sky-700 font-medium leading-tight" data-testid={`establishment-${o.id}`}>{o.establishment_name}</p>
                      )}
                      <p className="text-[11px] text-zinc-700 font-medium mt-0.5" data-testid={`patient-ref-${o.id}`}>{o.patient_reference}</p>
                      {(() => {
                        const opts = o.options || {};
                        const typeLabels = {
                          'ecouteur_deporte': 'Écouteur déporté',
                          'bte': 'BTE',
                          'silk': 'Intra Silk',
                          'integral': 'Intégral',
                        };
                        const typeRaw = o.embout_type || opts.embout_type;
                        const type = typeRaw ? (typeLabels[typeRaw] || typeRaw) : null;
                        const formeRaw = opts.forme_embout || opts.forme_embout_od;
                        // Pour Intra Silk, la forme est unique — pas d'info supplémentaire à afficher
                        const forme = formeRaw && formeRaw !== 'silk' ? getEmboutFormeLabel(formeRaw) : null;
                        if (opts.fabrication_type === 'obturateur') {
                          return <p className="text-[11px] text-violet-700 font-medium mt-0.5" data-testid={`row-type-${o.id}`}>Obturateur{opts.obturateur_type ? ` — ${opts.obturateur_type.replace(/_/g, ' ')}` : ''}</p>;
                        }
                        if (opts.fabrication_type === 'conformateur_oreille') {
                          return <p className="text-[11px] text-blue-700 font-medium mt-0.5" data-testid={`row-type-${o.id}`}>Conformateur d&apos;oreille</p>;
                        }
                        if (!type && !forme) return null;
                        return (
                          <p className="text-[11px] text-zinc-600 mt-0.5 capitalize" data-testid={`row-type-${o.id}`}>
                            {type && <span className="font-medium text-zinc-700">{type}</span>}
                            {type && forme && <span className="text-zinc-400"> • </span>}
                            {forme && <span>{forme}</span>}
                          </p>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="font-mono text-[11px] hidden xl:table-cell leading-tight">
                      {o.serial_number_left && (
                        <div className="flex items-center gap-1.5">
                          <span>L: {o.serial_number_left}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDownloadSerialPng(o, 'left'); }}
                            className="inline-flex items-center justify-center w-5 h-5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors"
                            title="Télécharger étiquette PNG (bleu)"
                            data-testid={`row-png-left-${o.id}`}
                          >
                            <Download size={11} weight="bold" />
                          </button>
                        </div>
                      )}
                      {o.serial_number_right && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span>R: {o.serial_number_right}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDownloadSerialPng(o, 'right'); }}
                            className="inline-flex items-center justify-center w-5 h-5 bg-red-600 hover:bg-red-700 text-white rounded-sm transition-colors"
                            title="Télécharger étiquette PNG (rouge)"
                            data-testid={`row-png-right-${o.id}`}
                          >
                            <Download size={11} weight="bold" />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(o.status, o.order_type, o)}
                      {o.wants_3d_validation && o.status !== 'completed' && o.status !== 'cancelled' && (
                        o.validation3d_done ? (
                          <span
                            className="block mt-1 px-1.5 py-0.5 bg-emerald-600 text-white text-[9px] font-bold rounded-sm tracking-wider uppercase text-center"
                            data-testid={`val3d-validated-badge-${o.id}`}
                            title="Le client a validé le modèle 3D — la fabrication peut être lancée"
                          >
                            Val. 3D validée
                          </span>
                        ) : (
                          <span
                            className="block mt-1 px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-sm tracking-wider uppercase text-center"
                            data-testid={`val3d-requested-badge-${o.id}`}
                            title="Le client a demandé une validation 3D — fabrication bloquée tant que non validée"
                          >
                            Val. 3D demandée
                          </span>
                        )
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-0.5 sm:gap-1 flex-wrap">
                        {/* Validation 3D button — visible only when client requested it */}
                        {o.wants_3d_validation && o.status !== 'completed' && o.status !== 'cancelled' && (!savBlock.blocked || isAdmin) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setVal3dDialogOrder(o)}
                            title="Uploader & envoyer le modèle 3D pour validation client"
                            data-testid={`val3d-row-btn-${o.id}`}
                            className="h-8 w-8 p-0 sm:h-9 sm:w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Cube size={16} weight="duotone" />
                          </Button>
                        )}

                        {/* Details button — blocked for non-SAV orders when SAV blocked (technician only) */}
                        {savBlock.blocked && !o.is_sav_refabrication && !isAdmin ? (
                          <Button variant="ghost" size="sm" disabled className="h-8 w-8 p-0 sm:h-9 sm:w-9 opacity-30 cursor-not-allowed" title="Bloque par SAV urgent" data-testid={`details-blocked-${o.id}`}><Eye size={16} /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => openDetailsDialog(o)} title="Voir détails" data-testid={`details-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><Eye size={16} /></Button>
                        )}

                        {/* SAV refabrication: show UPLOAD STL or uploaded status */}
                        {o.is_sav_refabrication && o.status !== 'completed' && o.status !== 'cancelled' && (
                          o.stl_files?.length > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-sm text-[10px] font-medium text-emerald-700" data-testid={`sav-stl-status-${o.id}`}>
                                <CheckCircle size={12} weight="fill" />
                                STL uploade — V{Math.max(...o.stl_files.map(f => f.version || 1))}
                              </span>
                              <Button size="sm" variant="outline" onClick={() => openSavStlDialog(o)}
                                className="rounded-sm text-[10px] h-7 px-2" data-testid={`sav-reupload-${o.id}`}>
                                <UploadSimple size={12} className="mr-1" />Nouvelle version
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" onClick={() => openSavStlDialog(o)}
                              className="rounded-sm bg-violet-600 hover:bg-violet-700 text-white text-[11px] sm:text-xs h-8 sm:h-9 px-2 sm:px-3 animate-pulse"
                              data-testid={`sav-upload-stl-${o.id}`}>
                              <UploadSimple size={14} className="mr-1" />
                              <span className="hidden sm:inline">UPLOAD STL</span>
                              <span className="sm:hidden">STL</span>
                            </Button>
                          )
                        )}

                        {/* Action buttons (Design / Fabrication / Prêt) — visible pour TOUTES les commandes
                            (normales ET SAV refab), uniquement bloquées si SAV-block actif (sauf admin) */}
                        {(!savBlock.blocked || isAdmin || o.is_sav_refabrication) && (
                          <>
                        {/* Edit button */}
                        {o.status !== 'completed' && o.status !== 'cancelled' && (
                          <Button variant="ghost" size="sm" onClick={() => handleEditOrder(o)} title="Modifier" data-testid={`edit-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9 text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                            <PencilSimple size={16} />
                          </Button>
                        )}

                        {o.stl_files?.length > 0 && <Button variant="ghost" size="sm" onClick={() => handleDownloadSTL(o)} title="Télécharger STL" data-testid={`download-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><Download size={16} /></Button>}

                        {/* Upload STL button — visible when order has no STL or needs more */}
                        {o.status !== 'completed' && o.status !== 'cancelled' && (
                          <Button variant="ghost" size="sm" onClick={() => triggerStlUpload(o.id)} title="Uploader STL" data-testid={`upload-stl-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9 text-violet-600 hover:text-violet-700 hover:bg-violet-50"><UploadSimple size={16} /></Button>
                        )}

                        {o.status === 'pending' && (
                          savBlock.blocked && !isAdmin ? (
                            <Button size="sm" disabled className="rounded-sm bg-zinc-400 text-[11px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3 cursor-not-allowed" data-testid={`blocked-design-${o.id}`} title="Bloque par SAV urgent">
                              <PencilSimple size={14} className="mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Bloque</span>
                            </Button>
                          ) : (
                            <Button size="sm" onClick={() => askConfirm('start-design', o.id)} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-[11px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3" data-testid={`start-design-${o.id}`}>
                              <PencilSimple size={14} className="mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Design</span>
                            </Button>
                          )
                        )}
                        {o.status === 'design' && (
                          o.wants_3d_validation && !o.validation3d_done ? (
                            <span title="Validation 3D en attente — le client doit valider le modèle 3D avant le lancement de la fabrication">
                              <Button size="sm" disabled className="rounded-sm bg-zinc-200 text-zinc-400 cursor-not-allowed text-[11px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3" data-testid={`fab-blocked-${o.id}`}>
                                <Hammer size={14} className="mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Fabrication</span>
                              </Button>
                            </span>
                          ) : (
                            <Button size="sm" onClick={() => askConfirm('start-fabrication', o.id)} className="rounded-sm bg-amber-500 hover:bg-amber-600 text-[11px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3" data-testid={`start-fab-${o.id}`}>
                              <Hammer size={14} className="mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Fabrication</span>
                            </Button>
                          )
                        )}
                        {o.status === 'in_progress' && (
                          <Button size="sm" onClick={() => askConfirm('ready', o.id)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-[11px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3" data-testid={`ready-${o.id}`}>
                            <PaperPlaneTilt size={14} className="mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Prêt</span>
                          </Button>
                        )}

                        {o.status === 'completed' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openBonLivraison(o.id)} title="Bon de Livraison" data-testid={`bl-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><File size={16} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => printProductLabel(o.id)} title="Étiquette produit" data-testid={`print-product-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><Printer size={16} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => printShippingLabel(o.id)} title="Étiquette adresse" data-testid={`print-shipping-${o.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><MapPin size={16} /></Button>
                          </>
                        )}

                        {o.status !== 'completed' && o.status !== 'cancelled' && (
                          <Button variant="ghost" size="sm" onClick={() => askConfirm('cancel', o.id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 w-8 p-0 sm:h-9 sm:w-9" title="Annuler" data-testid={`cancel-${o.id}`}>
                            <XCircle size={16} />
                          </Button>
                        )}

                        {o.status === 'cancelled' && user?.role === 'admin' && (
                          <Button variant="ghost" size="sm" onClick={() => askConfirm('delete', o.id)} className="text-red-700 hover:text-red-800 hover:bg-red-50 h-8 w-8 p-0 sm:h-9 sm:w-9" title="Supprimer" data-testid={`delete-${o.id}`}>
                            <Trash size={16} />
                          </Button>
                        )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-2 py-3 border-t border-zinc-200" data-testid="pagination">
              <span className="text-xs text-zinc-500">{pagination.total} commande(s) — Page {pagination.page}/{pagination.total_pages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)} className="rounded-sm h-7 px-2 text-xs">&laquo;</Button>
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="rounded-sm h-7 px-2 text-xs">&lsaquo;</Button>
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  const start = Math.max(1, Math.min(currentPage - 2, pagination.total_pages - 4));
                  const p = start + i;
                  if (p > pagination.total_pages) return null;
                  return (
                    <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm"
                      onClick={() => setCurrentPage(p)}
                      className={`rounded-sm h-7 w-7 p-0 text-xs ${p === currentPage ? 'bg-zinc-900 text-white' : ''}`}
                      data-testid={`page-${p}`}>{p}</Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={currentPage >= pagination.total_pages} onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))} className="rounded-sm h-7 px-2 text-xs">&rsaquo;</Button>
                <Button variant="outline" size="sm" disabled={currentPage >= pagination.total_pages} onClick={() => setCurrentPage(pagination.total_pages)} className="rounded-sm h-7 px-2 text-xs">&raquo;</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Démarrage de production : bac / lot actif / profil / opérateur */}
      <StartProductionDialog orderId={startProdOrderId} order={orders.find(o => o.id === startProdOrderId)} user={user} open={!!startProdOrderId} onOpenChange={(v) => { if (!v) setStartProdOrderId(null); }} onStarted={() => { fetchOrders(); fetchStats(); }} />

      {/* CQ-01 : contrôle final + photo obligatoire avant « Embout prêt » */}
      <Cq01Dialog orderId={cq01OrderId} order={orders.find(o => o.id === cq01OrderId)} open={!!cq01OrderId} onOpenChange={(v) => { if (!v) setCq01OrderId(null); }}
        onReady={(data) => { const o = orders.find(x => x.id === cq01OrderId); setSelectedOrder(o); setReadyResult(data); setReadyDialogOpen(true); fetchOrders(); fetchStats(); }} />

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmAction && confirmDialogLabels[confirmAction.type]?.title}</DialogTitle>
            <DialogDescription>{confirmAction && confirmDialogLabels[confirmAction.type]?.desc}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleConfirmAction} className={`rounded-sm text-white ${confirmAction && confirmDialogLabels[confirmAction.type]?.color}`} data-testid="confirm-action-btn">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ready / Completion Dialog */}
      <Dialog open={readyDialogOpen} onOpenChange={setReadyDialogOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PaperPlaneTilt size={24} className="text-emerald-600" />Commande prête à envoyer</DialogTitle>
          </DialogHeader>
          {readyResult && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
                <p className="font-medium text-emerald-800 mb-2">Actions effectuées :</p>
                <ul className="list-disc list-inside text-sm text-emerald-700">{readyResult.actions?.map((a) => <li key={a}>{a}</li>)}</ul>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setReadyDialogOpen(false)} className="rounded-sm">Fermer</Button>
            {selectedOrder && <Button onClick={() => openBonLivraison(selectedOrder.id)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700"><File size={16} className="mr-2" />BL</Button>}
            {selectedOrder && <Button onClick={() => printProductLabel(selectedOrder.id)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800"><Printer size={16} className="mr-2" />Produit</Button>}
            {selectedOrder && <Button onClick={() => { printShippingLabel(selectedOrder.id); setReadyDialogOpen(false); }} className="rounded-sm bg-zinc-700 hover:bg-zinc-600"><MapPin size={16} className="mr-2" />Adresse</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span className="flex items-center gap-2"><Eye size={24} className="text-zinc-900" />Détails de la commande</span>
              {detailsOrder && (
                <Button size="sm" variant="outline" onClick={handlePrintOrderSheet} className="rounded-sm text-xs font-normal" data-testid="print-order-sheet-btn">
                  <Printer size={14} className="mr-1" />Imprimer le bon
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {detailsOrder && (() => {
            const opts = detailsOrder.options || {};
            const matLabel = (m) => m === 'resine_3d' ? 'Résine 3D' : m === 'silicone' ? 'Silicone' : m === 'resine_souple' ? 'Résine souple' : m;
            const matiereLabel = matLabel(detailsOrder.material);
            // Défensif : certaines anciennes commandes ont `fabrication_type` non
            // renseigné. On considère un obturateur dès que l'un des champs
            // caractéristiques est présent, sinon des blocs « Appareil auditif »
            // s'affichent alors qu'aucun écouteur n'a été demandé (bug tech
            // rapporté 2026-02 : mention "Marque Phonak" sur un anti-bruit).
            const isObturateur = opts.fabrication_type === 'obturateur'
              || !!opts.obturateur_type
              || (typeof detailsOrder.product_lookup_key === 'string' && detailsOrder.product_lookup_key.startsWith('obturateur_'))
              || (typeof detailsOrder.product_name === 'string' && /obturateur|anti[- ]bruit|anti[- ]eau|bouchon musique|conformateur/i.test(detailsOrder.product_name));
            // Libellé synthétique du "Type" affiché en tête du récapitulatif —
            // évite au technicien de devoir scanner « Caractéristiques communes »
            // pour retrouver s'il s'agit d'un obturateur, d'un embout intégral, BTE, etc.
            const emboutTypeShort = opts.embout_type === 'ecouteur_deporte' ? 'Écouteur déporté (RIC)'
              : opts.embout_type === 'bte' ? 'BTE (Contour)'
              : opts.embout_type === 'silk' ? 'Intra (Silk)'
              : opts.embout_type === 'integral' ? 'Intégral (écouteur intégré)'
              : (opts.embout_type || '');
            const typeHeaderLabel = opts.fabrication_type === 'obturateur'
              ? (opts.obturateur_type ? `Obturateur — ${opts.obturateur_type.replace(/_/g, ' ')}` : 'Obturateur')
              : opts.fabrication_type === 'conformateur_oreille'
                ? "Conformateur d'oreille"
                : (emboutTypeShort || 'Embout auditif');
            return (
            <div className="space-y-4 sm:space-y-5 py-2" id="order-sheet-print-root" data-testid="order-sheet-content">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-zinc-50 p-3 sm:p-4 rounded-sm border border-zinc-200">
                  <p className="label-overline mb-1">Référence Patient</p>
                  <p className="font-bold text-base sm:text-lg" data-testid="detail-patient-ref">{detailsOrder.patient_reference}</p>
                  {opts.patient_type && <p className="text-sm text-zinc-500 mt-1 capitalize">Patient : {opts.patient_type}</p>}
                </div>
                <div className="bg-zinc-50 p-3 sm:p-4 rounded-sm border border-zinc-200">
                  <p className="label-overline mb-1">Client</p>
                  <p className="font-bold">{detailsOrder.client_name}</p>
                  <p className="text-sm text-zinc-500">{detailsOrder.client_company}</p>
                  {detailsOrder.establishment_name && <p className="text-sm text-sky-700 font-medium" data-testid="detail-establishment-name">Établissement : {detailsOrder.establishment_name}</p>}
                  {(detailsOrder.establishment_address || detailsOrder.client_address) && <p className="text-xs text-zinc-400 mt-1" data-testid="detail-delivery-address">{detailsOrder.establishment_address || detailsOrder.client_address}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {detailsOrder.serial_number_left && (
                  <div className="bg-zinc-900 text-white p-3 rounded-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1">N° Série OG</p>
                        {editingSerial ? (
                          <Input value={editSerialLeft} onChange={e => setEditSerialLeft(e.target.value)} className="font-mono text-base bg-zinc-800 border-zinc-700 text-white" data-testid="edit-serial-left" />
                        ) : (
                          <p className="font-mono text-base sm:text-lg break-all">{detailsOrder.serial_number_left}</p>
                        )}
                      </div>
                      {!editingSerial && (
                        <button
                          type="button"
                          onClick={() => handleDownloadSerialPng(detailsOrder, 'left')}
                          className="shrink-0 inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm transition-colors"
                          title="Télécharger l'étiquette PNG (bleu)"
                          data-testid="download-serial-png-left"
                        >
                          <Download size={12} />PNG
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {detailsOrder.serial_number_right && (
                  <div className="bg-zinc-900 text-white p-3 rounded-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1">N° Série OD</p>
                        {editingSerial ? (
                          <Input value={editSerialRight} onChange={e => setEditSerialRight(e.target.value)} className="font-mono text-base bg-zinc-800 border-zinc-700 text-white" data-testid="edit-serial-right" />
                        ) : (
                          <p className="font-mono text-base sm:text-lg break-all">{detailsOrder.serial_number_right}</p>
                        )}
                      </div>
                      {!editingSerial && (
                        <button
                          type="button"
                          onClick={() => handleDownloadSerialPng(detailsOrder, 'right')}
                          className="shrink-0 inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm transition-colors"
                          title="Télécharger l'étiquette PNG (rouge)"
                          data-testid="download-serial-png-right"
                        >
                          <Download size={12} />PNG
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Serial number edit buttons */}
              {detailsOrder.status !== 'completed' && detailsOrder.status !== 'cancelled' && (
                <div className="flex gap-2">
                  {editingSerial ? (
                    <>
                      <Button size="sm" onClick={saveSerial} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm" data-testid="save-serial-btn">Enregistrer</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingSerial(false)} className="rounded-sm">Annuler</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => startEditSerial(detailsOrder)} className="rounded-sm text-xs" data-testid="edit-serial-btn">
                      <PencilSimple size={14} className="mr-1" />Modifier le n° de série
                    </Button>
                  )}
                </div>
              )}
              {detailsOrder.status === 'completed' && (
                <p className="text-xs text-zinc-400 italic">Le numéro de série est verrouillé (commande terminée)</p>
              )}
              {detailsOrder.status !== 'draft' && (
                <div className="flex flex-wrap gap-2" data-testid="device-sheet-links">
                  {detailsOrder.serial_number_right && (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => navigate(`/device/${encodeURIComponent(detailsOrder.serial_number_right)}`)} data-testid="device-sheet-link-right">
                      <Barcode size={14} className="mr-1" />Fiche de fabrication OD
                    </Button>
                  )}
                  {detailsOrder.serial_number_left && (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => navigate(`/device/${encodeURIComponent(detailsOrder.serial_number_left)}`)} data-testid="device-sheet-link-left">
                      <Barcode size={14} className="mr-1" />Fiche de fabrication OG
                    </Button>
                  )}
                </div>
              )}

              {/* ===== NOTES / COMMENTAIRES — toujours affichés, juste sous les n° de série ===== */}
              <div className="border border-zinc-200 rounded-sm" data-testid="detail-notes-section">
                <div className="bg-zinc-50 px-4 py-2 rounded-t-sm border-b border-zinc-200">
                  <p className="font-heading font-semibold text-sm text-zinc-700">Notes / Commentaires</p>
                </div>
                <div className="p-3 sm:p-4 space-y-2">
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Notes / Instructions</p>
                    <p className={`text-sm whitespace-pre-wrap ${detailsOrder.notes ? '' : 'text-zinc-400 italic'}`} data-testid="detail-notes">{detailsOrder.notes || 'Aucune note'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Information complémentaire</p>
                    <p className={`text-sm whitespace-pre-wrap ${opts.info_complementaire ? 'text-amber-900' : 'text-zinc-400 italic'}`} data-testid="detail-info-complementaire">{opts.info_complementaire || 'Aucune'}</p>
                  </div>
                  {opts.envoi_courrier && (
                    <div className="bg-amber-50 border border-amber-300 p-2 rounded-sm flex items-center gap-2">
                      <Package size={16} className="text-amber-600" />
                      <p className="text-sm font-medium text-amber-800">Empreintes envoyées par courrier</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ===== OPTIONS — toujours affichées, sous les commentaires, fond bleu sombre ===== */}
              {(() => {
                const boolOpts = [];
                if (opts.obturateur_poignee) boolOpts.push('Poignée');
                if (opts.obturateur_cordon) boolOpts.push('Cordon de sécurité');
                if (opts.obturateur_etui) boolOpts.push('Étui');
                if (opts.finition_anallergique) boolOpts.push('Finition anallergique');
                if (opts.marquage_special) boolOpts.push(`Marquage: ${opts.marquage_special_texte || 'Oui'}`);
                if (opts.fournir_bushing_cerustop) boolOpts.push('Bushing Cerustop');
                const selOpts = (detailsOrder.selected_options || []).map(o => o.name);
                const allOpts = [...selOpts, ...boolOpts];
                return (
                  <div className="border border-blue-900 rounded-sm" data-testid="detail-options-section">
                    <div className="bg-blue-900 text-white px-4 py-2 rounded-t-sm">
                      <p className="font-heading font-semibold text-sm">Options ({allOpts.length})</p>
                    </div>
                    <div className="p-3 sm:p-4 bg-blue-50/40">
                      {allOpts.length === 0 ? (
                        <p className="text-sm text-zinc-400 italic" data-testid="detail-options-empty">Aucune option sélectionnée</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {allOpts.map((name) => (
                            <span key={name} className="px-3 py-1.5 bg-blue-900 text-white text-xs font-medium rounded-sm">{name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ===== AFFICHAGE PAR OREILLE ===== */}
              {detailsOrder.ear_side === 'both' ? (
                <>
                  {/* Alerte matières différentes OD/OG — visible immédiatement pour le tech */}
                  {opts.matiere_od && opts.matiere_og && opts.matiere_od !== opts.matiere_og && (
                    <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-sm p-3 flex items-start gap-2.5" data-testid="alert-matieres-differentes">
                      <Warning size={18} weight="fill" className="text-amber-700 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 text-sm">
                        <p className="font-semibold">Attention : matières différentes OD/OG</p>
                        <p className="text-xs mt-0.5">
                          Oreille droite : <strong>{matLabel(opts.matiere_od)}</strong>
                          {' · '}
                          Oreille gauche : <strong>{matLabel(opts.matiere_og)}</strong>
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Embout OD */}
                  <div className="border border-zinc-200 rounded-sm" data-testid="detail-ear-od">
                    <div className="bg-zinc-900 text-white px-4 py-2 rounded-t-sm flex items-center justify-between">
                      <p className="font-heading font-semibold text-sm">{opts.fabrication_type === 'obturateur' ? 'Obturateur OD (Oreille Droite)' : 'Embout OD (Oreille Droite)'}</p>
                      {detailsOrder.serial_number_right && <span className="font-mono text-xs text-zinc-400">{detailsOrder.serial_number_right}</span>}
                    </div>
                    <div className="p-3 sm:p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
                        <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Type</p><p className="font-semibold text-sm" data-testid="detail-type-od">{typeHeaderLabel}</p></div>
                        {!isObturateur && (
                          <div data-testid="detail-forme-od">
                            <p className="text-xs text-zinc-400 uppercase tracking-wider">Forme</p>
                            {(() => {
                              const f = opts.forme_embout_od || opts.forme_embout || '';
                              const label = getEmboutFormeLabel(f) || '-';
                              const imgSrc = getEmboutFormeImage(opts.embout_type, f);
                              return (
                                <div className="flex items-center gap-2 mt-1">
                                  {imgSrc && (
                                    <div className="w-14 h-14 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                                      <img src={imgSrc} alt={label} className="w-full h-full object-contain" loading="lazy" />
                                    </div>
                                  )}
                                  <p className="font-semibold text-sm leading-tight capitalize">{label}</p>
                                </div>
                              );
                            })()}
                            {(opts.forme_demi_epaulement_od || (opts.forme_demi_epaulement && !opts.forme_embout_og)) && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-sm" data-testid="modifier-demi-epaulement-od">+ Demi-épaulement</span>
                            )}
                            {(opts.forme_evidee_od || (opts.forme_evidee && !opts.forme_embout_og)) && (
                              <span className="inline-block mt-1 ml-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-sm" data-testid="modifier-evidee-od">+ Évidée</span>
                            )}
                          </div>
                        )}
                        {/* Cas bilatéral obturateur : ajout de la cellule Forme
                            (image + libellé) même en OD/OG, afin d'aligner l'UI
                            sur la version unilatérale. */}
                        {isObturateur && (opts.obturateur_forme || opts.obturateur_type) && (() => {
                          const illu = getObturateurIllustration(opts.obturateur_type, opts.obturateur_forme);
                          const label = getObturateurFormeLabel(opts.obturateur_type, opts.obturateur_forme);
                          if (!illu && !label) return null;
                          return (
                            <div data-testid="detail-obturateur-forme">
                              <p className="text-xs text-zinc-400 uppercase tracking-wider">Forme</p>
                              <div className="flex items-center gap-2 mt-1">
                                {illu?.image && (
                                  <div className="w-14 h-14 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                                    <img src={illu.image} alt={label || 'Obturateur'} className="w-full h-full object-contain" loading="lazy" />
                                  </div>
                                )}
                                <p className="font-semibold text-sm leading-tight capitalize">{label || '—'}</p>
                              </div>
                            </div>
                          );
                        })()}
                        <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Matière</p><p className="font-semibold text-sm">{matLabel(opts.matiere_od || detailsOrder.material)}</p></div>
                        {['silicone','resine_souple'].includes(opts.matiere_od || detailsOrder.material) && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Dureté</p><p className="font-semibold text-sm">{opts.durete_od || opts.durete || '40'} Shore</p></div>}
                        <div data-testid="detail-couleur-od">
                          <p className="text-xs text-zinc-400 uppercase tracking-wider">Couleur</p>
                          <p className="font-semibold text-sm flex items-center"><ColorSwatch color={detailsOrder.color} />{couleurLabel(detailsOrder.color)}</p>
                        </div>
                        {!isObturateur && (
                          <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Event</p><p className="font-semibold text-sm">{opts.event_od || 'Sans'}{opts.event_od && opts.event_od !== 'Sans' && opts.event_od !== 'MAX' ? ' mm' : ''}</p></div>
                        )}
                        {!isObturateur && opts.embout_type === 'ecouteur_deporte' && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Puissance</p><p className="font-semibold text-sm" data-testid="detail-puissance-od">{getPuissanceDisplay(opts, 'od') || '—'}</p></div>}
                      </div>
                    </div>
                  </div>

                  {/* Embout OG */}
                  <div className="border border-zinc-200 rounded-sm" data-testid="detail-ear-og">
                    <div className="bg-zinc-700 text-white px-4 py-2 rounded-t-sm flex items-center justify-between">
                      <p className="font-heading font-semibold text-sm">{opts.fabrication_type === 'obturateur' ? 'Obturateur OG (Oreille Gauche)' : 'Embout OG (Oreille Gauche)'}</p>
                      {detailsOrder.serial_number_left && <span className="font-mono text-xs text-zinc-300">{detailsOrder.serial_number_left}</span>}
                    </div>
                    <div className="p-3 sm:p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
                        <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Type</p><p className="font-semibold text-sm" data-testid="detail-type-og">{typeHeaderLabel}</p></div>
                        {!isObturateur && (
                          <div data-testid="detail-forme-og">
                            <p className="text-xs text-zinc-400 uppercase tracking-wider">Forme</p>
                            {(() => {
                              const f = opts.forme_embout_og || opts.forme_embout || '';
                              const label = getEmboutFormeLabel(f) || '-';
                              const imgSrc = getEmboutFormeImage(opts.embout_type, f);
                              return (
                                <div className="flex items-center gap-2 mt-1">
                                  {imgSrc && (
                                    <div className="w-14 h-14 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                                      <img src={imgSrc} alt={label} className="w-full h-full object-contain" loading="lazy" />
                                    </div>
                                  )}
                                  <p className="font-semibold text-sm leading-tight capitalize">{label}</p>
                                </div>
                              );
                            })()}
                            {(opts.forme_demi_epaulement_og || (opts.forme_demi_epaulement && !opts.forme_embout_og)) && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-sm" data-testid="modifier-demi-epaulement-og">+ Demi-épaulement</span>
                            )}
                            {opts.forme_evidee_og && (
                              <span className="inline-block mt-1 ml-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-sm" data-testid="modifier-evidee-og">+ Évidée</span>
                            )}
                          </div>
                        )}
                        {/* Cas bilatéral obturateur (OG) — symétrique à OD. */}
                        {isObturateur && (opts.obturateur_forme || opts.obturateur_type) && (() => {
                          const illu = getObturateurIllustration(opts.obturateur_type, opts.obturateur_forme);
                          const label = getObturateurFormeLabel(opts.obturateur_type, opts.obturateur_forme);
                          if (!illu && !label) return null;
                          return (
                            <div data-testid="detail-obturateur-forme">
                              <p className="text-xs text-zinc-400 uppercase tracking-wider">Forme</p>
                              <div className="flex items-center gap-2 mt-1">
                                {illu?.image && (
                                  <div className="w-14 h-14 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                                    <img src={illu.image} alt={label || 'Obturateur'} className="w-full h-full object-contain" loading="lazy" />
                                  </div>
                                )}
                                <p className="font-semibold text-sm leading-tight capitalize">{label || '—'}</p>
                              </div>
                            </div>
                          );
                        })()}
                        <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Matière</p><p className="font-semibold text-sm">{matLabel(opts.matiere_og || detailsOrder.material)}</p></div>
                        {['silicone','resine_souple'].includes(opts.matiere_og || detailsOrder.material) && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Dureté</p><p className="font-semibold text-sm">{opts.durete_og || opts.durete || '40'} Shore</p></div>}
                        <div data-testid="detail-couleur-og">
                          <p className="text-xs text-zinc-400 uppercase tracking-wider">Couleur</p>
                          <p className="font-semibold text-sm flex items-center"><ColorSwatch color={detailsOrder.color} />{couleurLabel(detailsOrder.color)}</p>
                        </div>
                        {!isObturateur && (
                          <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Event</p><p className="font-semibold text-sm">{opts.event_og || 'Sans'}{opts.event_og && opts.event_og !== 'Sans' && opts.event_og !== 'MAX' ? ' mm' : ''}</p></div>
                        )}
                        {!isObturateur && opts.embout_type === 'ecouteur_deporte' && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Puissance</p><p className="font-semibold text-sm" data-testid="detail-puissance-og">{getPuissanceDisplay(opts, 'og') || '—'}</p></div>}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Commande unilatérale - une seule section embout */
                <div className="border border-zinc-200 rounded-sm" data-testid="detail-ear-single">
                  <div className="bg-zinc-900 text-white px-4 py-2 rounded-t-sm flex items-center justify-between">
                    <p className="font-heading font-semibold text-sm">{opts.fabrication_type === 'obturateur' ? `Obturateur ${detailsOrder.ear_side === 'left' ? 'OG (Gauche)' : 'OD (Droit)'}` : `Embout ${detailsOrder.ear_side === 'left' ? 'OG (Gauche)' : 'OD (Droit)'}`}</p>
                    <span className="font-mono text-xs text-zinc-400">{detailsOrder.ear_side === 'left' ? detailsOrder.serial_number_left : detailsOrder.serial_number_right}</span>
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
                      <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Type</p><p className="font-semibold text-sm" data-testid="detail-type">{typeHeaderLabel}</p></div>
                      {/* Forme obturateur — obligatoire pour anti-bruit non filtré,
                          anti-bruit filtré et bouchon musique. Affichage avec image
                          si la forme est référencée dans OBTURATEUR_FORMES, sinon
                          fallback image type d'obturateur / texte pour ne pas perdre
                          l'info sur d'anciennes valeurs. */}
                      {isObturateur && (opts.obturateur_forme || opts.obturateur_type) && (() => {
                        const illu = getObturateurIllustration(opts.obturateur_type, opts.obturateur_forme);
                        const label = getObturateurFormeLabel(opts.obturateur_type, opts.obturateur_forme);
                        if (!illu && !label) return null;
                        return (
                          <div data-testid="detail-obturateur-forme">
                            <p className="text-xs text-zinc-400 uppercase tracking-wider">Forme</p>
                            <div className="flex items-center gap-2 mt-1">
                              {illu?.image && (
                                <div className="w-14 h-14 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                                  <img
                                    src={illu.image}
                                    alt={label || 'Obturateur'}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <p className="font-semibold text-sm leading-tight capitalize">{label || '—'}</p>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Atténuation / Filtre pour obturateurs anti-bruit filtré et bouchon musique */}
                      {isObturateur && opts.obturateur_attenuation && (
                        <div data-testid="detail-obturateur-attenuation">
                          <p className="text-xs text-zinc-400 uppercase tracking-wider">Filtre / Atténuation</p>
                          <p className="font-semibold text-sm">{getObturateurAttenuationLabel(opts.obturateur_attenuation) || opts.obturateur_attenuation}</p>
                        </div>
                      )}
                      {!isObturateur && opts.forme_embout && (
                        <div data-testid="detail-forme-single">
                          <p className="text-xs text-zinc-400 uppercase tracking-wider">Forme</p>
                          {(() => {
                            const f = opts.forme_embout;
                            const label = getEmboutFormeLabel(f);
                            const imgSrc = getEmboutFormeImage(opts.embout_type, f);
                            return (
                              <div className="flex items-center gap-2 mt-1">
                                {imgSrc && (
                                  <div className="w-14 h-14 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                                    <img src={imgSrc} alt={label} className="w-full h-full object-contain" loading="lazy" />
                                  </div>
                                )}
                                <p className="font-semibold text-sm leading-tight capitalize">{label}</p>
                              </div>
                            );
                          })()}
                          {opts.forme_demi_epaulement && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-sm" data-testid="modifier-demi-epaulement">+ Demi-épaulement</span>
                          )}
                          {opts.forme_evidee && (
                            <span className="inline-block mt-1 ml-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-sm" data-testid="modifier-evidee">+ Évidée</span>
                          )}
                        </div>
                      )}
                      <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Matière</p><p className="font-semibold text-sm">{matiereLabel}</p></div>
                      {['silicone','resine_souple'].includes(detailsOrder.material) && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Dureté</p><p className="font-semibold text-sm">{opts.durete ? `${opts.durete} Shore` : '—'}</p></div>}
                      <div data-testid="detail-couleur-single">
                        <p className="text-xs text-zinc-400 uppercase tracking-wider">Couleur</p>
                        <p className="font-semibold text-sm flex items-center"><ColorSwatch color={detailsOrder.color} />{couleurLabel(detailsOrder.color)}</p>
                      </div>
                      {!isObturateur && (
                        <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Event</p><p className="font-semibold text-sm">{(detailsOrder.ear_side === 'left' ? opts.event_og : opts.event_od) || 'Sans'}</p></div>
                      )}
                      {!isObturateur && opts.embout_type === 'ecouteur_deporte' && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Puissance</p><p className="font-semibold text-sm" data-testid="detail-puissance-single">{getPuissanceDisplay(opts) || '—'}</p></div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Caractéristiques communes */}
              <div className="border border-zinc-200 rounded-sm">
                <div className="bg-zinc-50 px-4 py-2 rounded-t-sm border-b border-zinc-200">
                  <p className="font-heading font-semibold text-sm text-zinc-700">Caractéristiques communes</p>
                </div>
                <div className="p-3 sm:p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
                    {/* « Côté » et « Couleur » sont affichés dans les blocs par oreille (titre + ligne Couleur) — pas de redondance ici. */}
                    <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Étanchéité</p><p className="font-semibold text-sm capitalize" data-testid="detail-etancheite">{opts.etancheite && opts.etancheite !== 'sans' ? opts.etancheite : 'Sans'}</p></div>
                    {opts.fabrication_type !== 'conformateur_oreille' && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Finition</p><p className="font-semibold text-sm capitalize" data-testid="detail-finition">{opts.finition ? `${opts.finition}${opts.finition_anallergique ? ' (Anallergique)' : ''}` : '—'}</p></div>}
                    <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Texture oreille</p><p className="font-semibold text-sm capitalize" data-testid="detail-texture">{opts.texture_oreille || '—'}</p></div>
                    {(() => {
                      const dd = detailsOrder.delivery_date || opts.delivery_date;
                      return (
                        <div>
                          <p className="text-xs text-zinc-400 uppercase tracking-wider">Livraison souhaitée</p>
                          <p className={`font-semibold text-sm ${dd ? 'text-red-700' : 'text-zinc-400'}`} data-testid="detail-delivery-date">{dd ? new Date(dd).toLocaleDateString('fr-FR') : 'N/D'}</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Conformateur d'oreille — description libre + photos illustratives */}
              {opts.fabrication_type === 'conformateur_oreille' && (
                <div className="border border-zinc-200 rounded-sm" data-testid="detail-conformateur-block">
                  <div className="bg-blue-50 px-4 py-2 rounded-t-sm border-b border-blue-200">
                    <p className="font-heading font-semibold text-sm text-blue-900">Conformateur d&apos;oreille — Besoin client</p>
                  </div>
                  <div className="p-3 sm:p-4 space-y-3">
                    {opts.conformateur_description ? (
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap" data-testid="detail-conformateur-description">{opts.conformateur_description}</p>
                    ) : (
                      <p className="text-xs italic text-zinc-400">Aucune description renseignée.</p>
                    )}
                    {Array.isArray(opts.conformateur_photo_ids) && opts.conformateur_photo_ids.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="detail-conformateur-photos">
                        {opts.conformateur_photo_ids.map((fid) => (
                          <div
                            key={fid}
                            className="block bg-zinc-100 border border-zinc-200 rounded-sm overflow-hidden aspect-square hover:border-zinc-400 transition-colors"
                          >
                            <AuthImage
                              url={`${API}/orders/conformateur-photo/${fid}`}
                              alt="Photo conformateur"
                              className="w-full h-full object-cover cursor-pointer"
                              onLoaded={(obj) => { /* preview loaded */ }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Appareil auditif — toujours affiché pour un RIC (valeurs « — » si non renseignées) */}
              {!isObturateur && opts.embout_type === 'ecouteur_deporte' && (
                <div className="border border-zinc-200 rounded-sm" data-testid="detail-appareil-section">
                  <div className="bg-zinc-50 px-4 py-2 rounded-t-sm border-b border-zinc-200">
                    <p className="font-heading font-semibold text-sm text-zinc-700">Appareil auditif</p>
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
                      <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Marque</p><p className="font-semibold text-sm" data-testid="detail-marque">{opts.marque || '—'}</p></div>
                      <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Modèle</p><p className="font-semibold text-sm" data-testid="detail-modele">{opts.modele || '—'}</p></div>
                      {opts.montage_type && (
                        <div className="col-span-2 md:col-span-3 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="recap-montage-type">
                          <p className="text-xs text-amber-800 uppercase tracking-wider font-bold">⚠️ Type de montage</p>
                          <p className="font-bold text-base text-amber-900">{MONTAGE_LABELS[opts.montage_type] || opts.montage_type}</p>
                          {opts.montage_type === 'montage_bague_3dsa' && opts.bague_3dsa_consent && (
                            <p className="text-xs text-amber-800 mt-1">
                              ✓ Consentement client au changement — option de repli : <strong>{MONTAGE_LABELS[opts.bague_3dsa_fallback] === 'Montage tube' ? 'Click tube' : (MONTAGE_LABELS[opts.bague_3dsa_fallback] || opts.bague_3dsa_fallback || '—')}</strong>
                            </p>
                          )}
                        </div>
                      )}
                      {opts.ecouteur_version && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Version écouteur</p><p className="font-semibold text-sm">{opts.ecouteur_version}</p></div>}
                      {opts.ecouteur_version_wsa && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Version écouteur</p><p className="font-semibold text-sm">{opts.ecouteur_version_wsa}</p></div>}
                      {opts.ecouteur_type && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Type écouteur</p><p className="font-semibold text-sm">{opts.ecouteur_type}</p></div>}
                      {/* « Puissance » et « Info écouteur » retirés : la puissance est déjà indiquée par oreille dans les blocs OD/OG. */}
                      <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Filtre</p><p className="font-semibold text-sm" data-testid="detail-filtre">{opts.filtre_type ? (opts.filtre_type === 'hf4' ? 'HF4' : 'CeruStop') : '—'}</p></div>
                      {opts.fournir_bushing && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Bushing</p><p className="font-semibold text-sm">Fourni par le client</p></div>}
                      {opts.bte_brand_specific_enabled && opts.bte_brand_specific_type && (
                        <div className="col-span-2"><p className="text-xs text-zinc-400 uppercase tracking-wider">Mini-tube marque</p>
                          <p className="font-semibold text-sm">{opts.bte_brand_specific_type === 'power_slim_tube' ? 'Power SlimTube' : `Corda MiniFit${opts.bte_corda_minifit_diameter ? ` ${opts.bte_corda_minifit_diameter} mm` : ''}`}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* BTE — Montage tube + brand-specific (rendered for BTE only) */}
              {!isObturateur && opts.embout_type === 'bte' && (
                <div className="border border-zinc-200 rounded-sm">
                  <div className="bg-zinc-50 px-4 py-2 rounded-t-sm border-b border-zinc-200">
                    <p className="font-heading font-semibold text-sm text-zinc-700">Options BTE</p>
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
                      {opts.marque && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Marque</p><p className="font-semibold text-sm">{opts.marque}</p></div>}
                      {opts.montage_type && (
                        <div className="col-span-2 md:col-span-3 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="recap-montage-type-bte">
                          <p className="text-xs text-amber-800 uppercase tracking-wider font-bold">⚠️ Type de montage</p>
                          <p className="font-bold text-base text-amber-900">{MONTAGE_LABELS[opts.montage_type] || opts.montage_type}</p>
                        </div>
                      )}
                      {opts.bte_brand_specific_enabled && opts.bte_brand_specific_type && (
                        <div className="col-span-2 md:col-span-3 bg-blue-50 border border-blue-300 rounded-sm px-3 py-2">
                          <p className="text-xs text-blue-800 uppercase tracking-wider font-bold">Mini-tube spécifique marque</p>
                          <p className="font-bold text-sm text-blue-900">{opts.bte_brand_specific_type === 'power_slim_tube' ? 'Power SlimTube' : `Corda MiniFit${opts.bte_corda_minifit_diameter ? ` ${opts.bte_corda_minifit_diameter} mm` : ''}`}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Embout intégral - Phonak intégré */}
              {!isObturateur && opts.embout_type === 'integral' && (
                <div className="border border-blue-200 rounded-sm bg-blue-50/30">
                  <div className="bg-blue-50 px-4 py-2 rounded-t-sm border-b border-blue-200">
                    <p className="font-heading font-semibold text-sm text-blue-700">Embout intégral (Phonak)</p>
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-3">
                      <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Marque</p><p className="font-semibold text-sm">Phonak</p></div>
                      {opts.ecouteur_version && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Version écouteur</p><p className="font-semibold text-sm">{opts.ecouteur_version}</p></div>}
                      {opts.ecouteur_puissance && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Puissance</p><p className="font-semibold text-sm">{opts.ecouteur_puissance}</p></div>}
                      {opts.integral_cable_length && <div><p className="text-xs text-zinc-400 uppercase tracking-wider">Longueur câble</p><p className="font-semibold text-sm">{opts.integral_cable_length}</p></div>}
                    </div>
                    <p className="text-[11px] text-blue-700 mt-3 italic">Délai de fabrication étendu : 5 jours ouvrés.</p>
                  </div>
                </div>
              )}

              {/* Options / commentaires : désormais affichés en tête (sous les n° de série) */}

              {detailsOrder.reused_impressions && (
                <div className="bg-emerald-50 border-2 border-emerald-400 p-3 rounded-sm flex items-center gap-2" data-testid="recap-reused-impressions">
                  <ClockCounterClockwise size={20} className="text-emerald-700" weight="bold" />
                  <div>
                    <p className="text-sm font-bold text-emerald-800">Empreintes réutilisées d&apos;une commande précédente</p>
                    <p className="text-xs text-emerald-700">Aucune nouvelle empreinte à attendre — les STL ont été copiés automatiquement.</p>
                  </div>
                </div>
              )}

              {detailsOrder.stl_files && detailsOrder.stl_files.length > 0 && (
                <div>
                  <p className="label-overline mb-2 flex items-center gap-2">
                    <File size={16} />
                    Fichiers STL ({detailsOrder.stl_files.length})
                  </p>
                  <div className="space-y-1.5">
                    {[...detailsOrder.stl_files].reverse().map((f, i) => (
                      <div key={f.file_id || f.filename || `stl-${i}`} className="flex items-center gap-2 text-sm bg-zinc-50 p-2.5 rounded-sm border border-zinc-200">
                        <File size={16} className="text-zinc-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs truncate">{(() => {
                              const serial = f.side === 'left' ? detailsOrder.serial_number_left : detailsOrder.serial_number_right;
                              const ref = (detailsOrder.patient_reference || '').trim().replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 30);
                              return serial && ref ? `${ref}_${serial}.stl` : (f.filename || 'fichier.stl');
                            })()}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${i === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-600'}`}>
                              V{f.version || detailsOrder.stl_files.length - i}
                            </span>
                            <span className="text-[10px] text-zinc-400">{f.side === 'left' ? 'OG' : 'OD'}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {f.uploaded_at && <span className="text-[10px] text-zinc-400">{new Date(f.uploaded_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                            {f.uploaded_by && <span className="text-[10px] text-zinc-400">{f.uploaded_by}</span>}
                          </div>
                        </div>
                        {/* Download button */}
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadSingleSTL(f)} title="Telecharger" className="h-7 w-7 p-0 flex-shrink-0" data-testid={`stl-download-${f.file_id}`}>
                          <Download size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation 3D Section */}
              {(detailsOrder.wants_3d_validation || detailsOrder.status !== 'cancelled') && (
                <div data-no-print="true"><Validation3DSection orderId={detailsOrder.id} order={detailsOrder} /></div>
              )}

              <div data-no-print="true">
                <p className="label-overline mb-3 flex items-center gap-2">
                  <ClockCounterClockwise size={16} />
                  Historique des actions
                </p>
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  {detailsOrder.action_history && detailsOrder.action_history.length > 0 ? (
                    <div className="divide-y divide-zinc-200">
                      {[...detailsOrder.action_history].reverse().map((action, idx) => (
                        <div key={`${action.timestamp}-${action.action}`} className="p-3 flex items-start gap-3 sm:gap-4">
                          <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-zinc-400"></div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{action.action}</p>
                            {action.details && <p className="text-xs text-zinc-500 truncate">{action.details}</p>}
                            <p className="text-xs text-zinc-400 mt-1">
                              {new Date(action.timestamp).toLocaleString('fr-FR')} - {action.user_name} ({action.user_role})
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-zinc-500 text-sm">Aucun historique disponible</div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)} className="rounded-sm">Fermer</Button>
            {detailsOrder?.stl_files?.length > 0 && (
              <Button onClick={() => handleDownloadSTL(detailsOrder)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800">
                <Download size={16} className="mr-2" />Télécharger STL
              </Button>
            )}
            <Button variant="outline" onClick={() => detailsOrder?.id && printProductLabel(detailsOrder.id)} className="rounded-sm" data-testid="print-product-label">
              <Printer size={16} className="mr-2" />Étiquette Produit
            </Button>
            <Button variant="outline" onClick={() => detailsOrder?.id && printShippingLabel(detailsOrder.id)} className="rounded-sm" data-testid="print-shipping-label">
              <Printer size={16} className="mr-2" />Étiquette Adresse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Order / Edit Order Dialog */}
      <ManualOrderDialog
        open={orderDialogOpen || !!editingOrder}
        onOpenChange={handleDialogClose}
        onSuccess={refreshAfterCreate}
        editOrder={editingOrder}
      />

      {/* Hidden file input for STL upload */}
      <input type="file" ref={stlInputRef} multiple accept=".stl" className="hidden" onChange={handleStlFileSelected} data-testid="stl-file-input" />

      {/* Validation 3D Dialog — direct access from row */}
      <Dialog open={!!val3dDialogOrder} onOpenChange={(open) => !open && setVal3dDialogOrder(null)}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="val3d-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cube size={22} weight="duotone" className="text-red-600" />
              Validation 3D — {val3dDialogOrder?.patient_reference || 'Commande'}
            </DialogTitle>
          </DialogHeader>
          {val3dDialogOrder && (
            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-sm text-sm" data-testid="val3d-dialog-banner">
                <p className="font-semibold text-red-900">Le client a demandé une validation 3D avant fabrication</p>
                <p className="text-red-700 text-xs mt-1">
                  Uploadez le(s) STL de prévisualisation, puis envoyez le lien au client. Une copie vous sera envoyée par email.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-zinc-500">Client</span><p className="font-medium text-sm">{val3dDialogOrder.client_name}</p></div>
                <div><span className="text-zinc-500">Produit</span><p className="font-medium text-sm">{val3dDialogOrder.product_name}</p></div>
                <div><span className="text-zinc-500">Côté</span><p className="font-medium text-sm capitalize">{val3dDialogOrder.ear_side === 'both' ? 'Bilatéral (D+G)' : val3dDialogOrder.ear_side === 'left' ? 'Gauche' : 'Droite'}</p></div>
                <div><span className="text-zinc-500">Email validation 3D</span><p className="font-medium text-sm break-all">{val3dDialogOrder.validation_email || val3dDialogOrder.client_email}</p></div>
              </div>
              <Validation3DSection orderId={val3dDialogOrder.id} order={val3dDialogOrder} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVal3dDialogOrder(null)} className="rounded-sm" data-testid="val3d-dialog-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SAV STL Finalization Dialog */}
      <Dialog open={savStlDialogOpen} onOpenChange={setSavStlDialogOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sav-stl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UploadSimple size={22} className="text-violet-600" />
              Finalisation SAV — Upload STL
            </DialogTitle>
          </DialogHeader>
          {savStlDialogOrder && (
            <div className="space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm text-sm">
                <p className="font-medium">{savStlDialogOrder.patient_reference}</p>
                <p className="text-xs text-zinc-500">{savStlDialogOrder.product_name} — {savStlDialogOrder.client_name}</p>
                <p className="text-xs text-zinc-400">Oreille : {savStlDialogOrder.ear_side === 'both' ? 'Bilateral' : savStlDialogOrder.ear_side === 'left' ? 'Gauche' : 'Droit'}</p>
              </div>

              <p className="text-xs text-zinc-500">Merci d&apos;uploader les STL du modele final redesigne</p>

              {/* Left STL */}
              {(savStlDialogOrder.ear_side === 'left' || savStlDialogOrder.ear_side === 'both') && (
                <div className={`p-3 border-2 rounded-sm ${savStlUploaded.left ? 'border-emerald-300 bg-emerald-50' : 'border-dashed border-zinc-300'}`}>
                  <p className="text-xs font-medium text-zinc-700 mb-2">STL Gauche (L)</p>
                  {savStlUploaded.left ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-emerald-700 text-sm">
                        <CheckCircle size={16} weight="fill" />
                        <span>{savStlLeft?.name || 'Uploade'}</span>
                        <span className="text-[10px] font-bold bg-emerald-200 px-1.5 py-0.5 rounded-sm">V{savStlUploaded.left}</span>
                      </div>
                      <label className="cursor-pointer text-[10px] text-zinc-500 underline hover:text-zinc-700">
                        Remplacer
                        <input type="file" accept=".stl" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) { setSavStlLeft(f); handleSavStlUploadSide(f, 'left'); } }} />
                      </label>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 cursor-pointer h-12 hover:bg-zinc-50 transition-colors">
                      <UploadSimple size={18} className="text-zinc-400" />
                      <span className="text-sm text-zinc-500">Selectionner STL gauche</span>
                      <input type="file" accept=".stl" className="hidden" data-testid="sav-stl-left-input"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setSavStlLeft(f); handleSavStlUploadSide(f, 'left'); } }} />
                    </label>
                  )}
                </div>
              )}

              {/* Right STL */}
              {(savStlDialogOrder.ear_side === 'right' || savStlDialogOrder.ear_side === 'both') && (
                <div className={`p-3 border-2 rounded-sm ${savStlUploaded.right ? 'border-emerald-300 bg-emerald-50' : 'border-dashed border-zinc-300'}`}>
                  <p className="text-xs font-medium text-zinc-700 mb-2">STL Droit (R)</p>
                  {savStlUploaded.right ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-emerald-700 text-sm">
                        <CheckCircle size={16} weight="fill" />
                        <span>{savStlRight?.name || 'Uploade'}</span>
                        <span className="text-[10px] font-bold bg-emerald-200 px-1.5 py-0.5 rounded-sm">V{savStlUploaded.right}</span>
                      </div>
                      <label className="cursor-pointer text-[10px] text-zinc-500 underline hover:text-zinc-700">
                        Remplacer
                        <input type="file" accept=".stl" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) { setSavStlRight(f); handleSavStlUploadSide(f, 'right'); } }} />
                      </label>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 cursor-pointer h-12 hover:bg-zinc-50 transition-colors">
                      <UploadSimple size={18} className="text-zinc-400" />
                      <span className="text-sm text-zinc-500">Selectionner STL droit</span>
                      <input type="file" accept=".stl" className="hidden" data-testid="sav-stl-right-input"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setSavStlRight(f); handleSavStlUploadSide(f, 'right'); } }} />
                    </label>
                  )}
                </div>
              )}

              {/* Comment */}
              <div>
                <p className="text-xs text-zinc-400 mb-1">Commentaire (optionnel)</p>
                <textarea value={savStlComment} onChange={(e) => setSavStlComment(e.target.value)}
                  className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-16 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="Ex: Design modifie selon retour client..." data-testid="sav-stl-comment" />
              </div>

              {/* Send to client */}
              <Button onClick={handleSavSendToClient}
                disabled={savStlSending || (!savStlUploaded.left && !savStlUploaded.right)}
                className="w-full rounded-sm bg-violet-600 hover:bg-violet-700 text-white h-11"
                data-testid="sav-send-to-client-btn">
                {savStlSending ? 'Envoi en cours...' : (
                  <><PaperPlaneTilt size={16} className="mr-2" />Envoyer le visuel au client</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Upload 3D Dialog */}
      <BatchUpload3DDialog
        open={batchUploadOpen}
        onOpenChange={setBatchUploadOpen}
        onSuccess={fetchOrders}
      />

      {/* SAV Dashboard Dialog */}
      <Dialog open={savDialogOpen} onOpenChange={(v) => { setSavDialogOpen(v); if (!v) setSavDetailItem(null); }}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sav-dashboard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {savDetailItem ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setSavDetailItem(null)} className="h-7 w-7 p-0 mr-1"><CaretUp size={16} className="rotate-[-90deg]" /></Button>
                  <Wrench size={22} className="text-amber-600" />
                  {savDetailItem.sav_number}
                </>
              ) : (
                <>
                  <Wrench size={22} className="text-amber-600" />
                  SAV en cours ({savOpenCount})
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* SAV DETAIL VIEW */}
          {savDetailItem ? (
            <div className="space-y-4" data-testid="sav-detail-view">
              {savDetailItem.is_internal_error && (
                <div className="bg-red-600 text-white px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider text-center">ERREUR INTERNE — SAV URGENT</div>
              )}
              <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Informations client</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-zinc-400 text-xs">Client</span><p className="font-medium">{savDetailItem.client_name}</p></div>
                  <div><span className="text-zinc-400 text-xs">Societe</span><p>{savDetailItem.company_name || '—'}</p></div>
                  <div><span className="text-zinc-400 text-xs">Email</span><p className="text-xs">{savDetailItem.client_email}</p></div>
                  <div><span className="text-zinc-400 text-xs">Patient</span><p className="font-medium">{savDetailItem.order_patient_ref}</p></div>
                  <div><span className="text-zinc-400 text-xs">Produit</span><p>{savDetailItem.product_name}</p></div>
                  <div><span className="text-zinc-400 text-xs">N° Serie</span><p className="font-mono">{savDetailItem.serial_number || '—'}</p></div>
                  <div><span className="text-zinc-400 text-xs">Oreille</span><p>{savDetailItem.ear_side === 'left' ? 'Gauche' : savDetailItem.ear_side === 'right' ? 'Droit' : 'Bilateral'}</p></div>
                  <div><span className="text-zinc-400 text-xs">Materiau</span><p>{savDetailItem.order_material || '—'}</p></div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Description du probleme</p>
                <p className="text-sm whitespace-pre-wrap bg-zinc-50 p-3 rounded-sm">{savDetailItem.description || 'Pas de description'}</p>
                <div className="flex gap-4 mt-2 text-xs text-zinc-500">
                  <span>Type: <strong>{savDetailItem.request_type?.replace(/_/g, ' ')}</strong></span>
                  <span>Date probleme: <strong>{savDetailItem.problem_date ? new Date(savDetailItem.problem_date).toLocaleDateString('fr-FR') : '—'}</strong></span>
                </div>
              </div>
              {savDetailItem.attachments?.length > 0 && (
                <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Photos / Pieces jointes ({savDetailItem.attachments.length})</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {savDetailItem.attachments.map(a => (
                      <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"
                        className="block border border-zinc-200 rounded-sm overflow-hidden hover:border-zinc-400 transition-colors">
                        {a.content_type?.startsWith('image/') ? (
                          <img src={a.url} alt={a.filename} className="w-full h-32 object-cover" />
                        ) : (
                          <div className="p-3 text-center">
                            <File size={24} className="mx-auto mb-1 text-zinc-400" />
                            <p className="text-xs truncate">{a.filename}</p>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {savDetailItem.decision && (
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
                  <p className="text-xs text-emerald-600 uppercase tracking-wider mb-2">Decision</p>
                  <p className="text-sm font-bold">{savDetailItem.decision.type?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-zinc-600 mt-1">{savDetailItem.decision.justification}</p>
                </div>
              )}
            </div>
          ) : (
            /* SAV LIST VIEW */
            <>
          {savLoading ? (
            <div className="p-8 text-center text-zinc-400">Chargement...</div>
          ) : savList.filter(s => s.status !== 'cloturee').length === 0 ? (
            <div className="p-8 text-center text-zinc-400">
              <Wrench size={40} className="mx-auto mb-3 text-zinc-300" />
              <p>Aucune demande SAV en cours</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="sav-dashboard-list">
              {savList
                .filter(s => s.status !== 'cloturee')
                .sort((a, b) => {
                  if (a.is_internal_error && !b.is_internal_error) return -1;
                  if (!a.is_internal_error && b.is_internal_error) return 1;
                  if (a.urgency === 'urgent' && b.urgency !== 'urgent') return -1;
                  if (a.urgency !== 'urgent' && b.urgency === 'urgent') return 1;
                  return 0;
                })
                .map(sav => (
                <div key={sav.id} onClick={() => openSavDetail(sav.id)}
                  className={`p-3 border rounded-sm cursor-pointer hover:shadow-md transition-shadow ${sav.is_internal_error ? 'border-red-400 bg-red-50' : sav.urgency === 'urgent' ? 'border-amber-300 bg-amber-50' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}
                  data-testid={`sav-item-${sav.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-xs">{sav.sav_number}</span>
                        {sav.is_internal_error && (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-600 text-white rounded-sm animate-pulse">URGENT</span>
                        )}
                        {!sav.is_internal_error && sav.urgency === 'urgent' && (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-500 text-white rounded-sm">Urgent</span>
                        )}
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${
                          sav.status === 'demande_envoyee' ? 'bg-blue-100 text-blue-800' :
                          sav.status === 'solution_validee' ? 'bg-emerald-100 text-emerald-800' :
                          sav.status === 'en_cours_analyse' ? 'bg-amber-100 text-amber-800' :
                          'bg-zinc-100 text-zinc-600'
                        }`}>
                          {sav.status === 'demande_envoyee' ? 'Demande' :
                           sav.status === 'en_cours_analyse' ? 'Analyse' :
                           sav.status === 'solution_validee' ? 'Valide' :
                           sav.status === 'en_fabrication' ? 'Fabrication' :
                           sav.status?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{sav.client_name}{sav.company_name ? ` — ${sav.company_name}` : ''}</p>
                      <p className="text-xs text-zinc-500">{sav.product_name} | {sav.order_patient_ref} | {sav.serial_number || '—'}</p>
                      {sav.description && <p className="text-xs text-zinc-400 mt-1 truncate">{sav.description}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-zinc-400">{new Date(sav.created_at).toLocaleDateString('fr-FR')}</p>
                      {sav.decision && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm mt-1 inline-block ${
                          sav.decision.type === 'refabrication_gratuite' ? 'bg-emerald-100 text-emerald-700' :
                          sav.decision.type === 'refabrication_payante' ? 'bg-rose-100 text-rose-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {sav.decision.type === 'refabrication_gratuite' ? 'Gratuite' :
                           sav.decision.type === 'refabrication_payante' ? 'Payante' : 'Geste'}
                        </span>
                      )}
                      {!sav.decision && <span className="text-[10px] text-orange-500 font-medium">Sans decision</span>}
                      <Eye size={14} className="text-zinc-300 mt-1 ml-auto" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavDialogOpen(false)} className="rounded-sm">Fermer</Button>
            {isAdmin && (
              <Link to="/admin" onClick={() => setSavDialogOpen(false)}>
                <Button className="rounded-sm bg-zinc-900 hover:bg-zinc-800">
                  <Wrench size={16} className="mr-2" />Gestion SAV complete
                </Button>
              </Link>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
